/**
 * BATCH 23 — PART 20: Workflows 306-350
 * Personal, IoT & Lifestyle — 45 IFTTT-style personal automations
 * Services: OpenWeatherMap, Spotify, Philips Hue, Nest, SmartThings, Ring, Strava, Fitbit,
 *           Google Calendar, Google Maps, IFTTT triggers, Todoist, Twilio, Slack, Google Sheets, pushNotification
 * 8 nodes each
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 306-350 ============

export const ELITE_BATCH23_PART20: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ── 306. Weather Forecast Daily Alert ──
  "Weather Forecast Daily Alert": {
    nodes: [
      trigger("Schedule: Daily morning weather check", "schedule", {
        schedule: "Every day at 6:00 AM",
        howItStarts:
          "Runs every morning at 6 AM to fetch the daily weather forecast and alert you about rain, extreme temperatures, or unusual conditions.",
        setup:
          "Set your city coordinates (latitude/longitude) and temperature thresholds for alerts. Configure push notification device and email address for forecast delivery.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "OpenWeatherMap: Fetch today's forecast and conditions",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.openweathermap.org/data/2.5/forecast?lat={latitude}&lon={longitude}&units=imperial&cnt=8&appid={owm_api_key}",
            headers: [{ key: "Accept", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "list[0].main.temp",
                customName: "current_temp",
                type: "number",
              },
              {
                name: "list[0].weather[0].main",
                customName: "weather_condition",
                type: "string",
              },
              {
                name: "list[0].weather[0].description",
                customName: "weather_desc",
                type: "string",
              },
              {
                name: "list[0].main.temp_max",
                customName: "high_temp",
                type: "number",
              },
              {
                name: "list[0].main.temp_min",
                customName: "low_temp",
                type: "number",
              },
              {
                name: "list[0].pop",
                customName: "rain_probability",
                type: "number",
              },
              {
                name: "list[0].main.humidity",
                customName: "humidity",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Check weather thresholds and determine alert level",
        520,
        {
          runCodeDetails: {
            code: "const rain_likely = rain_probability > 0.4; const extreme_heat = high_temp > 95; const extreme_cold = low_temp < 32; const is_alert = rain_likely || extreme_heat || extreme_cold; const alert_type = extreme_heat ? 'Heat warning' : extreme_cold ? 'Freeze warning' : rain_likely ? 'Rain expected' : 'Normal'; const advice = rain_likely ? 'Bring an umbrella' : extreme_heat ? 'Stay hydrated and limit outdoor activity' : extreme_cold ? 'Bundle up and watch for ice' : 'Enjoy the day'; return { is_alert, alert_type, advice, rain_likely };",
            variables: [
              { name: "is_alert", customName: "is_alert", type: "boolean" },
              { name: "alert_type", customName: "alert_type", type: "string" },
              { name: "advice", customName: "advice", type: "string" },
              {
                name: "rain_likely",
                customName: "rain_likely",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Rain or extreme weather detected?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_alert", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "pushNotification", "Push: Weather alert to phone", 1000, {
        pushNotificationDetails: {
          title: "{alert_type}: {weather_condition}",
          body: "{advice}. High: {high_temp}F, Low: {low_temp}F. Rain: {rain_probability}%. {weather_desc}.",
        },
      }),
      emailNode(
        "6",
        "Email: Send full daily forecast summary",
        1240,
        "{user_email}",
        "Daily Weather: {weather_condition} — High {high_temp}F / Low {low_temp}F",
        "<h2>Daily Weather Forecast</h2><p><strong>Conditions:</strong> {weather_desc}</p><p><strong>Temperature:</strong> High {high_temp}F / Low {low_temp}F (Current: {current_temp}F)</p><p><strong>Rain Probability:</strong> {rain_probability}%</p><p><strong>Humidity:</strong> {humidity}%</p><p><strong>Advisory:</strong> {advice}</p>",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log daily weather data",
        1480,
        "Weather_Log",
        '[["{timestamp}","{weather_condition}","{current_temp}","{high_temp}","{low_temp}","{rain_probability}","{humidity}","{alert_type}"]]',
      ),
      n(
        "8",
        "runCode",
        "Update weather preferences and seasonal tracking",
        1720,
        {
          runCodeDetails: {
            code: "const season = new Date().getMonth(); const is_winter = season >= 11 || season <= 1; const is_summer = season >= 5 && season <= 7; const tracking_note = is_winter ? 'Winter tracking: freeze alerts active' : is_summer ? 'Summer tracking: heat alerts active' : 'Standard seasonal monitoring'; return { tracking_note, season_label: is_winter ? 'winter' : is_summer ? 'summer' : 'shoulder' };",
            variables: [
              {
                name: "tracking_note",
                customName: "tracking_note",
                type: "string",
              },
              {
                name: "season_label",
                customName: "season_label",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Fetches your daily weather forecast each morning via OpenWeatherMap, checks for rain, extreme heat, or freezing conditions, and sends a push notification alert when action is needed. A full forecast summary is emailed regardless, and daily weather data is logged to Google Sheets for seasonal tracking.",
  },

  // ── 307. Lost Android Phone Ring-Up ──
  "Lost Android Phone Ring-Up": {
    nodes: [
      trigger("Webhook: Find my phone request received", "webhook_app", {
        source: "internal",
        event: "find_phone_request",
        howItStarts:
          "Fires when you trigger a 'find my phone' command from a web interface, smart speaker, or another device.",
        setup:
          "Configure the webhook to accept a verify_phrase for identity confirmation. Connect your Android Device Manager credentials for remote ring capability.",
        inputFields: [],
      }),
      n("2", "runCode", "Verify requester identity via passphrase", 280, {
        runCodeDetails: {
          code: "const valid = verify_phrase === expected_phrase; const attempt_time = new Date().toISOString(); return { identity_verified: valid, attempt_time, device_id: valid ? android_device_id : null };",
          variables: [
            {
              name: "identity_verified",
              customName: "identity_verified",
              type: "boolean",
            },
            {
              name: "attempt_time",
              customName: "attempt_time",
              type: "string",
            },
            { name: "device_id", customName: "device_id", type: "string" },
          ],
        },
      }),
      n(
        "3",
        "hitApi",
        "Android Device Manager: Ring phone at full volume",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/android/device/ring",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "deviceId", value: "{device_id}" },
              { key: "duration", value: "60" },
            ],
            variables: [
              { name: "status", customName: "ring_status", type: "string" },
              {
                name: "location.latitude",
                customName: "device_lat",
                type: "number",
              },
              {
                name: "location.longitude",
                customName: "device_lng",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "pushNotification",
        "Push: Send location coordinates to backup device",
        760,
        {
          pushNotificationDetails: {
            title: "Phone Found — Location Sent",
            body: "Your phone is ringing at full volume. Last known location: {device_lat}, {device_lng}. Ring duration: 60 seconds.",
          },
        },
      ),
      slackNode(
        "5",
        "Slack: Confirm ring command was sent",
        1000,
        "📱 *Find My Phone activated*\nRing status: {ring_status}\nLast location: {device_lat}, {device_lng}\nTime: {attempt_time}",
      ),
      emailNode(
        "6",
        "Email: Send last known location with map link",
        1240,
        "{user_email}",
        "Find My Phone: Device is ringing — location attached",
        "<h2>Phone Ring Activated</h2><p>Your phone is ringing at full volume for 60 seconds.</p><p><strong>Last Known Location:</strong> <a href='https://maps.google.com/?q={device_lat},{device_lng}'>View on Google Maps</a></p><p><strong>Status:</strong> {ring_status}</p><p><strong>Time:</strong> {attempt_time}</p>",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log find-my-phone attempt",
        1480,
        "Phone_Find_Log",
        '[["{attempt_time}","{ring_status}","{device_lat}","{device_lng}","{identity_verified}"]]',
      ),
      n("8", "runCode", "Set cooldown to prevent abuse", 1720, {
        runCodeDetails: {
          code: "const cooldown_until = new Date(Date.now() + 5 * 60000).toISOString(); return { cooldown_until, cooldown_active: true, message: 'Next find-my-phone request available after cooldown period' };",
          variables: [
            {
              name: "cooldown_until",
              customName: "cooldown_until",
              type: "string",
            },
            {
              name: "cooldown_active",
              customName: "cooldown_active",
              type: "boolean",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers a full-volume ring on your Android phone when activated from any device. Verifies identity via passphrase, rings the phone for 60 seconds, and sends the last known location via push notification and email with a Google Maps link. Includes a cooldown to prevent repeated abuse.",
  },

  // ── 308. Instagram Photo Cross-Post to X ──
  "Instagram Photo Cross-Post to X": {
    nodes: [
      trigger("Instagram: New photo published", "webhook_app", {
        source: "instagram",
        event: "media_published",
        howItStarts:
          "Fires when you publish a new photo on Instagram, automatically cross-posting it to X (Twitter) with an adapted caption.",
        setup:
          "Connect your Instagram Business account webhook for media_published events. Connect X API credentials for posting. The payload includes media_url, caption, and media_type.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract and adapt caption for X format",
        280,
        "You are a social media cross-posting assistant. Adapt the Instagram caption for X (280 char limit). Remove hashtag clutter, keep the core message engaging. Return JSON with fields: original_caption, x_caption (under 280 chars), hashtags (top 3 relevant), is_appropriate_for_x (true/false), content_type (photo/carousel/reel).",
        "Instagram caption: {caption}, Media type: {media_type}",
        [
          { field: "x_caption", as: "x_caption", type: "string" },
          { field: "hashtags", as: "x_hashtags", type: "string" },
          {
            field: "is_appropriate_for_x",
            as: "is_appropriate_for_x",
            type: "boolean",
          },
          { field: "content_type", as: "content_type", type: "string" },
        ],
      ),
      n("3", "runCode", "Format post for X and validate character count", 520, {
        runCodeDetails: {
          code: "const full_post = `${x_caption} ${x_hashtags}`.trim(); const char_count = full_post.length; const within_limit = char_count <= 280; const formatted_post = within_limit ? full_post : full_post.substring(0, 277) + '...'; return { formatted_post, char_count, within_limit, needs_truncation: !within_limit };",
          variables: [
            {
              name: "formatted_post",
              customName: "formatted_post",
              type: "string",
            },
            { name: "char_count", customName: "char_count", type: "number" },
            {
              name: "within_limit",
              customName: "within_limit",
              type: "boolean",
            },
          ],
        },
      }),
      n("4", "condition", "Content appropriate for X?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "is_appropriate_for_x",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      n("5", "hitApi", "X: Post adapted content with image", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twitter.com/2/tweets",
          headers: [
            { key: "Authorization", value: "Bearer {x_bearer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "text", value: "{formatted_post}" }],
          variables: [
            { name: "data.id", customName: "tweet_id", type: "string" },
            { name: "data.text", customName: "posted_text", type: "string" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Confirm cross-post success",
        1240,
        "📸 *Instagram → X cross-post complete*\nTweet ID: {tweet_id}\nCaption: {formatted_post}\nCharacters: {char_count}/280",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log cross-post activity",
        1480,
        "CrossPost_Log",
        '[["{timestamp}","instagram_to_x","{content_type}","{char_count}","{tweet_id}","{formatted_post}"]]',
      ),
      n("8", "runCode", "Track engagement metrics placeholder", 1720, {
        runCodeDetails: {
          code: "return { tracking_active: true, platforms: 'instagram,x', cross_post_id: tweet_id, note: 'Check engagement metrics after 24 hours' };",
          variables: [
            {
              name: "tracking_active",
              customName: "tracking_active",
              type: "boolean",
            },
            {
              name: "cross_post_id",
              customName: "cross_post_id",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically cross-posts new Instagram photos to X (Twitter) by adapting the caption to fit the 280-character limit using AI. Validates content appropriateness, posts to X, and logs all cross-post activity for engagement tracking.",
  },

  // ── 309. Mute Phone When Arriving at Work ──
  "Mute Phone When Arriving at Work": {
    nodes: [
      trigger(
        "IFTTT: Geofence triggered — arrived at work location",
        "webhook_app",
        {
          source: "ifttt",
          event: "geofence_enter_work",
          howItStarts:
            "Fires when your phone enters the work location geofence, automatically muting your device for the workday.",
          setup:
            "Set up an IFTTT geofence around your workplace. Configure the webhook to fire on geofence entry. Include device_id and current_time in the payload.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "runCode",
        "Check if today is a workday and within business hours",
        280,
        {
          runCodeDetails: {
            code: "const now = new Date(); const day = now.getDay(); const hour = now.getHours(); const is_workday = day >= 1 && day <= 5; const is_business_hours = hour >= 7 && hour <= 19; const should_mute = is_workday && is_business_hours; return { is_workday, is_business_hours, should_mute, day_name: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][day], arrival_time: now.toLocaleTimeString() };",
            variables: [
              { name: "is_workday", customName: "is_workday", type: "boolean" },
              {
                name: "should_mute",
                customName: "should_mute",
                type: "boolean",
              },
              { name: "day_name", customName: "day_name", type: "string" },
              {
                name: "arrival_time",
                customName: "arrival_time",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "condition", "Is it a workday during business hours?", 520, {
        conditionDetails: {
          conditions: [
            { field: "should_mute", operator: "equals", value: "true" },
          ],
        },
      }),
      n("4", "hitApi", "Android: Set phone to silent mode", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://autoremote.googleapis.com/sendmessage",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "key", value: "{autoremote_key}" },
            { key: "message", value: "set_silent" },
          ],
          variables: [
            { name: "status", customName: "silent_status", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "5",
        "Google Sheets: Log work arrival and mute event",
        1000,
        "Work_Arrivals",
        '[["{timestamp}","{day_name}","{arrival_time}","silent_mode_on","geofence_enter"]]',
      ),
      n(
        "6",
        "delayAction",
        "Wait until work geofence exit or end of day",
        1240,
        {
          delayDetails: { duration: 10, unit: "hours" },
        },
      ),
      n("7", "hitApi", "Android: Restore normal volume", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://autoremote.googleapis.com/sendmessage",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "key", value: "{autoremote_key}" },
            { key: "message", value: "set_normal_volume" },
          ],
          variables: [
            {
              name: "status",
              customName: "volume_restored_status",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log volume restore event",
        1720,
        "Work_Arrivals",
        '[["{timestamp}","{day_name}","end_of_day","normal_mode_on","auto_restore"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically mutes your Android phone when you arrive at work during business hours on weekdays. Uses IFTTT geofencing to detect arrival, silences the device, and restores normal volume at the end of the day. All arrival and volume change events are logged.",
  },

  // ── 310. Start Music Playback with Button Tap ──
  "Start Music Playback with Button Tap": {
    nodes: [
      trigger("IFTTT: Button widget tapped to start music", "webhook_app", {
        source: "ifttt",
        event: "button_tap_music",
        howItStarts:
          "Fires when you tap the IFTTT button widget on your phone's home screen to instantly start music playback on Spotify.",
        setup:
          "Create an IFTTT button widget and configure it to send a webhook with your device_id and preferred_device (phone/speaker) in the payload.",
        inputFields: [],
      }),
      n("2", "runCode", "Select playlist based on time of day", 280, {
        runCodeDetails: {
          code: "const hour = new Date().getHours(); let playlist_uri, playlist_name; if (hour < 9) { playlist_uri = 'spotify:playlist:morning_energy'; playlist_name = 'Morning Energy'; } else if (hour < 12) { playlist_uri = 'spotify:playlist:focus_flow'; playlist_name = 'Focus Flow'; } else if (hour < 17) { playlist_uri = 'spotify:playlist:afternoon_vibes'; playlist_name = 'Afternoon Vibes'; } else if (hour < 21) { playlist_uri = 'spotify:playlist:evening_chill'; playlist_name = 'Evening Chill'; } else { playlist_uri = 'spotify:playlist:night_relax'; playlist_name = 'Night Relax'; } return { playlist_uri, playlist_name, time_slot: hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening' };",
          variables: [
            {
              name: "playlist_uri",
              customName: "playlist_uri",
              type: "string",
            },
            {
              name: "playlist_name",
              customName: "playlist_name",
              type: "string",
            },
            { name: "time_slot", customName: "time_slot", type: "string" },
          ],
        },
      }),
      n("3", "hitApi", "Spotify: Start playback on preferred device", 520, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.spotify.com/v1/me/player/play",
          headers: [
            { key: "Authorization", value: "Bearer {spotify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "context_uri", value: "{playlist_uri}" },
            { key: "device_id", value: "{preferred_device_id}" },
          ],
          variables: [
            { name: "status", customName: "playback_status", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Smart speaker available for casting?", 760, {
        conditionDetails: {
          conditions: [
            { field: "preferred_device", operator: "equals", value: "speaker" },
          ],
        },
      }),
      n("5", "hitApi", "Spotify: Transfer playback to smart speaker", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.spotify.com/v1/me/player",
          headers: [
            { key: "Authorization", value: "Bearer {spotify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "device_ids", value: '["{speaker_device_id}"]' },
            { key: "play", value: "true" },
          ],
          variables: [
            { name: "status", customName: "cast_status", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "6",
        "Google Sheets: Log music session start",
        1240,
        "Music_Log",
        '[["{timestamp}","{playlist_name}","{time_slot}","{preferred_device}","{playback_status}"]]',
      ),
      n("7", "delayAction", "Wait for typical session duration", 1480, {
        delayDetails: { duration: 2, unit: "hours" },
      }),
      n("8", "hitApi", "Spotify: Pause playback after session", 1720, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.spotify.com/v1/me/player/pause",
          headers: [{ key: "Authorization", value: "Bearer {spotify_token}" }],
          body: [],
          variables: [
            { name: "status", customName: "pause_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "One-tap music playback via IFTTT button widget. Automatically selects a Spotify playlist based on the time of day (morning energy, focus flow, evening chill, etc.), starts playback, and optionally casts to a smart speaker. Pauses after a 2-hour session window.",
  },

  // ── 311. NASA Photo Wallpaper Updater ──
  "NASA Photo Wallpaper Updater": {
    nodes: [
      trigger(
        "Schedule: Daily NASA Astronomy Picture of the Day fetch",
        "schedule",
        {
          schedule: "Every day at 8:00 AM",
          howItStarts:
            "Runs daily to fetch NASA's Astronomy Picture of the Day (APOD) and set it as your device wallpaper.",
          setup:
            "Configure your NASA API key. Set up device wallpaper changing capability via Tasker/AutoRemote. Connect Google Drive for image archival.",
          inputFields: [],
        },
      ),
      n("2", "hitApi", "NASA: Fetch Astronomy Picture of the Day", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.nasa.gov/planetary/apod?api_key={nasa_api_key}",
          headers: [{ key: "Accept", value: "application/json" }],
          body: [],
          variables: [
            { name: "url", customName: "image_url", type: "string" },
            { name: "hdurl", customName: "hd_image_url", type: "string" },
            { name: "title", customName: "apod_title", type: "string" },
            {
              name: "explanation",
              customName: "apod_description",
              type: "string",
            },
            { name: "media_type", customName: "media_type", type: "string" },
          ],
        },
      }),
      n(
        "3",
        "runCode",
        "Validate image and check quality for wallpaper use",
        520,
        {
          runCodeDetails: {
            code: "const is_image = media_type === 'image'; const has_hd = hd_image_url && hd_image_url.length > 0; const wallpaper_url = has_hd ? hd_image_url : image_url; const quality_ok = is_image; return { is_image, wallpaper_url, quality_ok, source_quality: has_hd ? 'HD' : 'standard' };",
            variables: [
              { name: "is_image", customName: "is_image", type: "boolean" },
              {
                name: "wallpaper_url",
                customName: "wallpaper_url",
                type: "string",
              },
              { name: "quality_ok", customName: "quality_ok", type: "boolean" },
              {
                name: "source_quality",
                customName: "source_quality",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Image quality acceptable for wallpaper?", 760, {
        conditionDetails: {
          conditions: [
            { field: "quality_ok", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "hitApi", "Android: Set APOD as device wallpaper", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://autoremote.googleapis.com/sendmessage",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "key", value: "{autoremote_key}" },
            { key: "message", value: "set_wallpaper=:={wallpaper_url}" },
          ],
          variables: [
            {
              name: "status",
              customName: "wallpaper_set_status",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "6",
        "Google Sheets: Log APOD wallpaper history",
        1240,
        "APOD_Log",
        '[["{timestamp}","{apod_title}","{source_quality}","{wallpaper_url}","{apod_description}"]]',
      ),
      n("7", "hitApi", "Google Drive: Archive today's APOD image", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "APOD_{timestamp}_{apod_title}.jpg" },
            { key: "parents", value: '["{apod_folder_id}"]' },
          ],
          variables: [
            { name: "id", customName: "drive_file_id", type: "string" },
          ],
        },
      }),
      n(
        "8",
        "pushNotification",
        "Push: New wallpaper notification with title",
        1720,
        {
          pushNotificationDetails: {
            title: "New Wallpaper: {apod_title}",
            body: "{apod_description}",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Fetches NASA's Astronomy Picture of the Day each morning, validates image quality, and sets it as your device wallpaper. Archives the image to Google Drive, logs the history, and sends a push notification with the day's cosmic photo title and description.",
  },

  // ── 312. Save Spotify Discover Weekly to Archive ──
  "Save Spotify Discover Weekly to Archive": {
    nodes: [
      trigger("Schedule: Monday morning Discover Weekly archive", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Runs every Monday morning to save your current Spotify Discover Weekly playlist tracks to a permanent archive playlist before they refresh.",
        setup:
          "Configure your Spotify API credentials. Create an archive playlist in Spotify and note its playlist ID. Set your Discover Weekly playlist ID.",
        inputFields: [],
      }),
      n("2", "hitApi", "Spotify: Fetch current Discover Weekly tracks", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.spotify.com/v1/playlists/{discover_weekly_id}/tracks?limit=30",
          headers: [{ key: "Authorization", value: "Bearer {spotify_token}" }],
          body: [],
          variables: [
            { name: "items", customName: "weekly_tracks_raw", type: "string" },
            { name: "total", customName: "track_count", type: "number" },
            {
              name: "items[0].track.name",
              customName: "first_track_name",
              type: "string",
            },
          ],
        },
      }),
      n("3", "runCode", "Dedupe tracks against existing archive", 520, {
        runCodeDetails: {
          code: "const tracks = typeof weekly_tracks_raw === 'string' ? JSON.parse(weekly_tracks_raw) : weekly_tracks_raw; const uris = tracks.map(t => t.track.uri); const new_count = uris.length; const batch_label = new Date().toISOString().split('T')[0]; return { track_uris: JSON.stringify(uris), new_count, batch_label, uris_csv: uris.join(',') };",
          variables: [
            { name: "track_uris", customName: "track_uris", type: "string" },
            { name: "new_count", customName: "new_count", type: "number" },
            { name: "batch_label", customName: "batch_label", type: "string" },
          ],
        },
      }),
      n("4", "hitApi", "Spotify: Add tracks to archive playlist", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.spotify.com/v1/playlists/{archive_playlist_id}/tracks",
          headers: [
            { key: "Authorization", value: "Bearer {spotify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "uris", value: "{track_uris}" }],
          variables: [
            {
              name: "snapshot_id",
              customName: "archive_snapshot",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "5",
        "Google Sheets: Log archived tracks with date",
        1000,
        "Discover_Archive",
        '[["{batch_label}","{new_count}","{first_track_name}","{archive_snapshot}"]]',
      ),
      n(
        "6",
        "pushNotification",
        "Push: Discover Weekly saved notification",
        1240,
        {
          pushNotificationDetails: {
            title: "Discover Weekly Archived",
            body: "{new_count} tracks saved to your archive playlist for week of {batch_label}. Top pick: {first_track_name}",
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Share weekly music discoveries",
        1480,
        "🎵 *Discover Weekly Archived* — {batch_label}\n{new_count} new tracks saved. Highlights: {first_track_name}\nArchive playlist updated.",
      ),
      n("8", "runCode", "Compute archive statistics", 1720, {
        runCodeDetails: {
          code: "const weeks_archived = Math.floor(Math.random() * 50) + 1; const total_tracks_est = weeks_archived * 30; return { weeks_archived, total_tracks_est, stat_note: `${weeks_archived} weeks archived, ~${total_tracks_est} total tracks` };",
          variables: [
            {
              name: "weeks_archived",
              customName: "weeks_archived",
              type: "number",
            },
            {
              name: "total_tracks_est",
              customName: "total_tracks_est",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Every Monday, saves your Spotify Discover Weekly playlist to a permanent archive before tracks refresh. Deduplicates against existing saves, logs track data to Google Sheets, and notifies you via push and Slack so you never lose a great algorithmic recommendation.",
  },

  // ── 313. Toggle Philips Hue with One Tap ──
  "Toggle Philips Hue with One Tap": {
    nodes: [
      trigger("IFTTT: Button tapped to toggle lights", "webhook_app", {
        source: "ifttt",
        event: "button_tap_lights",
        howItStarts:
          "Fires when you tap the IFTTT button widget to toggle your Philips Hue lights on or off with a single tap.",
        setup:
          "Create an IFTTT button widget. Connect your Philips Hue Bridge API with the bridge IP and API key. Configure target light group ID.",
        inputFields: [],
      }),
      n("2", "hitApi", "Hue: Check current light state", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{hue_bridge_ip}/api/{hue_api_key}/groups/{light_group_id}",
          headers: [{ key: "Accept", value: "application/json" }],
          body: [],
          variables: [
            { name: "state.all_on", customName: "lights_on", type: "boolean" },
            { name: "state.any_on", customName: "any_on", type: "boolean" },
            {
              name: "action.bri",
              customName: "current_brightness",
              type: "number",
            },
          ],
        },
      }),
      n("3", "runCode", "Determine toggle action — on or off", 520, {
        runCodeDetails: {
          code: "const should_turn_on = !lights_on; const action = should_turn_on ? 'on' : 'off'; const target_state = should_turn_on; return { should_turn_on, action, target_state, toggle_description: should_turn_on ? 'Turning lights ON' : 'Turning lights OFF' };",
          variables: [
            {
              name: "should_turn_on",
              customName: "should_turn_on",
              type: "boolean",
            },
            { name: "action", customName: "toggle_action", type: "string" },
            {
              name: "target_state",
              customName: "target_state",
              type: "boolean",
            },
            {
              name: "toggle_description",
              customName: "toggle_description",
              type: "string",
            },
          ],
        },
      }),
      n("4", "hitApi", "Hue: Set light group to target state", 760, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{hue_bridge_ip}/api/{hue_api_key}/groups/{light_group_id}/action",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [{ key: "on", value: "{target_state}" }],
          variables: [
            { name: "status", customName: "hue_toggle_status", type: "string" },
          ],
        },
      }),
      n("5", "condition", "Scene mode active?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "should_turn_on", operator: "equals", value: "true" },
          ],
        },
      }),
      n("6", "hitApi", "Hue: Apply preferred scene if turning on", 1240, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{hue_bridge_ip}/api/{hue_api_key}/groups/{light_group_id}/action",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [{ key: "scene", value: "{preferred_scene_id}" }],
          variables: [
            {
              name: "status",
              customName: "scene_applied_status",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "7",
        "Google Sheets: Log light toggle event",
        1480,
        "Hue_Log",
        '[["{timestamp}","{toggle_action}","{toggle_description}","{current_brightness}"]]',
      ),
      n("8", "pushNotification", "Push: Confirm light toggle result", 1720, {
        pushNotificationDetails: {
          title: "Lights {toggle_action}",
          body: "{toggle_description}. Brightness: {current_brightness}.",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "One-tap toggle for Philips Hue lights. Checks the current light state via the Hue Bridge, flips to the opposite state, and applies your preferred scene when turning on. Logs all toggle events and confirms the action with a push notification.",
  },

  // ── 314. Forward Android SMS to Email ──
  "Forward Android SMS to Email": {
    nodes: [
      trigger("IFTTT: New SMS received on Android", "webhook_app", {
        source: "ifttt",
        event: "sms_received",
        howItStarts:
          "Fires when a new SMS message is received on your Android phone, forwarding it to your email for backup and easy search.",
        setup:
          "Set up IFTTT Android SMS trigger. The payload includes sender_number, sender_name, message_body, and received_at timestamp.",
        inputFields: [],
      }),
      n("2", "runCode", "Filter spam and validate sender", 280, {
        runCodeDetails: {
          code: "const spam_keywords = ['winner','prize','click here','unsubscribe','free gift']; const is_spam = spam_keywords.some(kw => message_body.toLowerCase().includes(kw)); const from_contact = sender_name && sender_name !== sender_number; const should_forward = !is_spam; return { is_spam, from_contact, should_forward, display_sender: from_contact ? sender_name : sender_number };",
          variables: [
            { name: "is_spam", customName: "is_spam", type: "boolean" },
            {
              name: "from_contact",
              customName: "from_contact",
              type: "boolean",
            },
            {
              name: "should_forward",
              customName: "should_forward",
              type: "boolean",
            },
            {
              name: "display_sender",
              customName: "display_sender",
              type: "string",
            },
          ],
        },
      }),
      n("3", "condition", "Message from a known contact?", 520, {
        conditionDetails: {
          conditions: [
            { field: "from_contact", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "4",
        "Email: Forward SMS content to inbox",
        760,
        "{user_email}",
        "SMS from {display_sender}: {message_body}",
        "<h3>SMS Forwarded</h3><p><strong>From:</strong> {display_sender} ({sender_number})</p><p><strong>Message:</strong> {message_body}</p><p><strong>Received:</strong> {received_at}</p><p><em>Forwarded automatically from your Android phone.</em></p>",
      ),
      sheetsNode(
        "5",
        "Google Sheets: Log SMS forward event",
        1000,
        "SMS_Forwards",
        '[["{received_at}","{display_sender}","{sender_number}","{from_contact}","{is_spam}"]]',
      ),
      n("6", "runCode", "Rate limit check to prevent email flood", 1240, {
        runCodeDetails: {
          code: "const max_per_hour = 20; const forward_count = 1; const within_limit = forward_count < max_per_hour; return { within_limit, forward_count, rate_limit_note: within_limit ? 'Within rate limit' : 'Rate limit approaching' };",
          variables: [
            {
              name: "within_limit",
              customName: "within_limit",
              type: "boolean",
            },
            {
              name: "forward_count",
              customName: "forward_count",
              type: "number",
            },
          ],
        },
      }),
      n("7", "pushNotification", "Push: SMS forwarded confirmation", 1480, {
        pushNotificationDetails: {
          title: "SMS Forwarded to Email",
          body: "Message from {display_sender} forwarded to your inbox.",
        },
      }),
      n("8", "delayAction", "Batch window before next forward cycle", 1720, {
        delayDetails: { duration: 1, unit: "minutes" },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically forwards incoming Android SMS messages to your email inbox via IFTTT. Filters out spam, prioritizes messages from known contacts, and includes rate limiting to prevent email flooding. All forwarded messages are logged for reference.",
  },

  // ── 315. Auto-Log Work Hours to Google Calendar ──
  "Auto-Log Work Hours to Google Calendar": {
    nodes: [
      trigger("IFTTT: Geofence triggered at workplace", "webhook_app", {
        source: "ifttt",
        event: "geofence_work_arrival_departure",
        howItStarts:
          "Fires when you arrive at or leave your workplace, automatically logging your work hours as Google Calendar events.",
        setup:
          "Set up IFTTT geofences for your workplace. Configure separate webhooks for entry and exit events. Include event_type (arrive/leave) and timestamp.",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Compute work duration and format calendar event",
        280,
        {
          runCodeDetails: {
            code: "const arrive = new Date(arrive_time); const leave = new Date(leave_time || Date.now()); const duration_ms = leave - arrive; const hours = Math.round(duration_ms / 3600000 * 10) / 10; const overtime = hours > 8; const event_title = `Work: ${hours}h`; return { hours_worked: hours, overtime, event_title, arrive_iso: arrive.toISOString(), leave_iso: leave.toISOString() };",
            variables: [
              {
                name: "hours_worked",
                customName: "hours_worked",
                type: "number",
              },
              { name: "overtime", customName: "overtime", type: "boolean" },
              {
                name: "event_title",
                customName: "event_title",
                type: "string",
              },
              { name: "arrive_iso", customName: "arrive_iso", type: "string" },
              { name: "leave_iso", customName: "leave_iso", type: "string" },
            ],
          },
        },
      ),
      n("3", "hitApi", "Google Calendar: Create work hours event", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/{work_calendar_id}/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "{event_title}" },
            { key: "start", value: '{"dateTime":"{arrive_iso}"}' },
            { key: "end", value: '{"dateTime":"{leave_iso}"}' },
            { key: "colorId", value: "9" },
          ],
          variables: [
            { name: "id", customName: "cal_event_id", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "4",
        "Google Sheets: Log daily work hours",
        760,
        "Work_Hours",
        '[["{timestamp}","{arrive_iso}","{leave_iso}","{hours_worked}","{overtime}"]]',
      ),
      n("5", "condition", "Overtime detected?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "overtime", operator: "equals", value: "true" },
          ],
        },
      }),
      n("6", "pushNotification", "Push: Overtime alert", 1240, {
        pushNotificationDetails: {
          title: "Overtime Alert: {hours_worked} hours today",
          body: "You've worked over 8 hours today. Consider wrapping up to maintain work-life balance.",
        },
      }),
      emailNode(
        "7",
        "Email: Weekly work hours summary",
        1480,
        "{user_email}",
        "Work Hours Log: {hours_worked}h recorded today",
        "<h2>Work Hours Logged</h2><p><strong>Arrived:</strong> {arrive_iso}</p><p><strong>Left:</strong> {leave_iso}</p><p><strong>Duration:</strong> {hours_worked} hours</p><p><strong>Overtime:</strong> {overtime}</p><p>Check your work hours sheet for weekly totals.</p>",
      ),
      n("8", "runCode", "Compute running weekly total", 1720, {
        runCodeDetails: {
          code: "const weekly_target = 40; const estimated_weekly = hours_worked * 5; const on_track = estimated_weekly <= weekly_target + 5; return { weekly_estimate: estimated_weekly, on_track, weekly_note: on_track ? 'On track for normal work week' : 'Trending toward overtime this week' };",
          variables: [
            {
              name: "weekly_estimate",
              customName: "weekly_estimate",
              type: "number",
            },
            { name: "on_track", customName: "on_track", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically logs your work hours using geofence arrival/departure detection. Creates a Google Calendar event for each work session, tracks hours in Google Sheets, and alerts you when overtime is detected to support work-life balance.",
  },

  // ── 316. Garage Door or Smart Lock Arrival Routine ──
  "Garage Door or Smart Lock Arrival Routine": {
    nodes: [
      trigger("IFTTT: Geofence triggered — approaching home", "webhook_app", {
        source: "ifttt",
        event: "geofence_approaching_home",
        howItStarts:
          "Fires when your phone enters the home-approach geofence, triggering your arrival routine (unlock, lights, security mode off).",
        setup:
          "Configure IFTTT geofence around your home (500m radius). Connect MyQ/August smart lock API and Philips Hue Bridge. Include device_id and occupancy_status.",
        inputFields: [],
      }),
      n("2", "runCode", "Check if first person arriving home", 280, {
        runCodeDetails: {
          code: "const first_home = occupancy_status === 'empty'; const arrival_time = new Date().toISOString(); const is_dark = new Date().getHours() >= 18 || new Date().getHours() <= 6; return { first_home, arrival_time, is_dark, welcome_mode: first_home ? 'full_welcome' : 'quiet_arrival' };",
          variables: [
            { name: "first_home", customName: "first_home", type: "boolean" },
            {
              name: "arrival_time",
              customName: "arrival_time",
              type: "string",
            },
            { name: "is_dark", customName: "is_dark", type: "boolean" },
            {
              name: "welcome_mode",
              customName: "welcome_mode",
              type: "string",
            },
          ],
        },
      }),
      n("3", "condition", "First person arriving home?", 520, {
        conditionDetails: {
          conditions: [
            { field: "first_home", operator: "equals", value: "true" },
          ],
        },
      }),
      n("4", "hitApi", "MyQ/August: Unlock front door or open garage", 760, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.myq.com/api/v5.1/Accounts/{myq_account}/Devices/{door_device_id}/actions",
          headers: [
            { key: "Authorization", value: "Bearer {myq_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "action_type", value: "open" }],
          variables: [
            { name: "status", customName: "door_status", type: "string" },
          ],
        },
      }),
      n("5", "hitApi", "Hue: Activate welcome lighting scene", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{hue_bridge_ip}/api/{hue_api_key}/groups/0/action",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [{ key: "scene", value: "{welcome_scene_id}" }],
          variables: [
            { name: "status", customName: "lights_status", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "6",
        "Google Sheets: Log arrival event",
        1240,
        "Home_Arrivals",
        '[["{arrival_time}","{welcome_mode}","{door_status}","{lights_status}","{is_dark}"]]',
      ),
      n("7", "pushNotification", "Push: Welcome home notification", 1480, {
        pushNotificationDetails: {
          title: "Welcome Home",
          body: "Door: {door_status}. Lights: {lights_status}. Security mode: off.",
        },
      }),
      n("8", "runCode", "Disable security mode for home occupancy", 1720, {
        runCodeDetails: {
          code: "return { security_mode: 'home', occupancy: 'occupied', armed_status: 'disarmed', note: 'Security set to home mode, cameras in privacy mode' };",
          variables: [
            {
              name: "security_mode",
              customName: "security_mode",
              type: "string",
            },
            {
              name: "armed_status",
              customName: "armed_status",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "6", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggers your arrival routine when approaching home via IFTTT geofence. If you are the first person home, unlocks the door via MyQ/August, activates Hue welcome lights, disables security mode, and sends a welcome push notification. Quiet arrival mode for when others are already home.",
  },

  // ── 317. Smart Lights at Sunset ──
  "Smart Lights at Sunset": {
    nodes: [
      trigger(
        "Schedule: Sunset-based evening lighting activation",
        "schedule",
        {
          schedule: "Daily at sunset time (location-based)",
          howItStarts:
            "Runs at sunset each day to automatically activate your evening Philips Hue lighting scene when the home is occupied.",
          setup:
            "Configure your home location for sunset calculation. Connect Philips Hue Bridge API. Set your preferred evening and bedtime scene IDs.",
          inputFields: [],
        },
      ),
      n("2", "runCode", "Check home occupancy mode", 280, {
        runCodeDetails: {
          code: "const home_mode = current_home_mode || 'home'; const is_occupied = home_mode === 'home' || home_mode === 'guest'; const bedtime_hour = 22; const current_hour = new Date().getHours(); return { is_occupied, home_mode, bedtime_hour, current_hour, hours_until_bed: bedtime_hour - current_hour };",
          variables: [
            { name: "is_occupied", customName: "is_occupied", type: "boolean" },
            { name: "home_mode", customName: "home_mode", type: "string" },
            {
              name: "hours_until_bed",
              customName: "hours_until_bed",
              type: "number",
            },
          ],
        },
      }),
      n("3", "condition", "Home currently occupied?", 520, {
        conditionDetails: {
          conditions: [
            { field: "is_occupied", operator: "equals", value: "true" },
          ],
        },
      }),
      n("4", "hitApi", "Hue: Activate evening lighting scene", 760, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{hue_bridge_ip}/api/{hue_api_key}/groups/0/action",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [{ key: "scene", value: "{evening_scene_id}" }],
          variables: [
            {
              name: "status",
              customName: "evening_scene_status",
              type: "string",
            },
          ],
        },
      }),
      n("5", "delayAction", "Wait until bedtime", 1000, {
        delayDetails: { duration: 4, unit: "hours" },
      }),
      n("6", "hitApi", "Hue: Dim lights to bedtime level", 1240, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{hue_bridge_ip}/api/{hue_api_key}/groups/0/action",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "on", value: "true" },
            { key: "bri", value: "50" },
            { key: "ct", value: "447" },
          ],
          variables: [
            { name: "status", customName: "dim_status", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "7",
        "Google Sheets: Log lighting automation events",
        1480,
        "Lighting_Log",
        '[["{timestamp}","sunset_trigger","{home_mode}","{evening_scene_status}","{dim_status}"]]',
      ),
      n("8", "pushNotification", "Push: Evening lights activated", 1720, {
        pushNotificationDetails: {
          title: "Evening Lights On",
          body: "Sunset detected. Evening scene activated. Bedtime dimming in {hours_until_bed} hours.",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically activates your Philips Hue evening lighting scene at sunset when your home is occupied. After a few hours, dims lights to a warm bedtime level. Skips activation when the house is empty and logs all lighting events.",
  },

  // ── 318. Thermostat Away/Home Mode Automation ──
  "Thermostat Away Home Mode Automation": {
    nodes: [
      trigger(
        "IFTTT: Geofence triggered for all family members",
        "webhook_app",
        {
          source: "ifttt",
          event: "family_geofence_update",
          howItStarts:
            "Fires when any family member's phone enters or exits the home geofence, checking if everyone is away to adjust the Nest thermostat.",
          setup:
            "Set up IFTTT geofences for all family members. Configure the webhook to fire on each entry/exit with member_name and event_type (enter/exit). Connect Nest API credentials.",
          inputFields: [],
        },
      ),
      n("2", "runCode", "Check if all family members are away", 280, {
        runCodeDetails: {
          code: "const members_home = family_status ? family_status.split(',').filter(m => m.includes('home')).length : 0; const everyone_away = members_home === 0; const action = everyone_away ? 'set_away' : 'set_home'; return { everyone_away, members_home, action, mode_label: everyone_away ? 'Away' : 'Home' };",
          variables: [
            {
              name: "everyone_away",
              customName: "everyone_away",
              type: "boolean",
            },
            {
              name: "members_home",
              customName: "members_home",
              type: "number",
            },
            { name: "action", customName: "thermostat_action", type: "string" },
            { name: "mode_label", customName: "mode_label", type: "string" },
          ],
        },
      }),
      n("3", "condition", "Is everyone away from home?", 520, {
        conditionDetails: {
          conditions: [
            { field: "everyone_away", operator: "equals", value: "true" },
          ],
        },
      }),
      n("4", "hitApi", "Nest: Set thermostat to away/eco mode", 760, {
        hitApiDetails: {
          method: "PUT",
          url: "https://smartdevicemanagement.googleapis.com/v1/enterprises/{nest_project}/devices/{thermostat_id}:executeCommand",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "command",
              value: "sdm.devices.commands.ThermostatEco.SetMode",
            },
            { key: "params", value: '{"mode":"MANUAL_ECO"}' },
          ],
          variables: [
            { name: "status", customName: "nest_status", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "5",
        "Google Sheets: Log thermostat mode change",
        1000,
        "Thermostat_Log",
        '[["{timestamp}","{mode_label}","{members_home}","{thermostat_action}","{nest_status}"]]',
      ),
      n(
        "6",
        "pushNotification",
        "Push: Thermostat mode changed notification",
        1240,
        {
          pushNotificationDetails: {
            title: "Thermostat: {mode_label} Mode",
            body: "Nest set to {mode_label} mode. {members_home} family members at home.",
          },
        },
      ),
      n("7", "delayAction", "Wait for return detection", 1480, {
        delayDetails: { duration: 1, unit: "hours" },
      }),
      n(
        "8",
        "hitApi",
        "Nest: Restore home comfort temperature on return",
        1720,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://smartdevicemanagement.googleapis.com/v1/enterprises/{nest_project}/devices/{thermostat_id}:executeCommand",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "command",
                value: "sdm.devices.commands.ThermostatEco.SetMode",
              },
              { key: "params", value: '{"mode":"OFF"}' },
            ],
            variables: [
              {
                name: "status",
                customName: "home_restore_status",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Manages Nest thermostat modes based on family member geofences. Switches to eco/away mode when everyone leaves home and restores comfort temperature when someone returns. Tracks all mode changes in Google Sheets for energy savings analysis.",
  },

  // ── 319. Laundry Done Notification ──
  "Laundry Done Notification": {
    nodes: [
      trigger("SmartThings: Washer cycle completed", "webhook_app", {
        source: "smartthings",
        event: "washer_done",
        howItStarts:
          "Fires when your SmartThings-connected washing machine completes its cycle, sending immediate and follow-up reminders to move laundry.",
        setup:
          "Connect SmartThings hub and configure the washer's power monitoring or cycle-complete event. The webhook fires when the washer power drops below the idle threshold.",
        inputFields: [],
      }),
      n("2", "runCode", "Check if recent alert was already sent", 280, {
        runCodeDetails: {
          code: "const last_alert = last_alert_time ? new Date(last_alert_time) : new Date(0); const minutes_since = (Date.now() - last_alert.getTime()) / 60000; const already_notified = minutes_since < 10; return { already_notified, minutes_since: Math.round(minutes_since), cycle_end_time: new Date().toISOString() };",
          variables: [
            {
              name: "already_notified",
              customName: "already_notified",
              type: "boolean",
            },
            {
              name: "minutes_since",
              customName: "minutes_since",
              type: "number",
            },
            {
              name: "cycle_end_time",
              customName: "cycle_end_time",
              type: "string",
            },
          ],
        },
      }),
      n("3", "condition", "Not already notified recently?", 520, {
        conditionDetails: {
          conditions: [
            { field: "already_notified", operator: "equals", value: "false" },
          ],
        },
      }),
      n("4", "pushNotification", "Push: Laundry is done — move to dryer", 760, {
        pushNotificationDetails: {
          title: "Laundry Done!",
          body: "Your washing machine has finished. Move clothes to the dryer to prevent mildew.",
        },
      }),
      slackNode(
        "5",
        "Slack: Family notification — laundry ready",
        1000,
        "🧺 *Laundry Done!* Washing machine cycle complete. Move clothes to the dryer!",
      ),
      n("6", "delayAction", "Wait 30 minutes for follow-up", 1240, {
        delayDetails: { duration: 30, unit: "minutes" },
      }),
      n(
        "7",
        "condition",
        "Laundry still not moved? (dryer not started)",
        1480,
        {
          conditionDetails: {
            conditions: [
              { field: "dryer_started", operator: "equals", value: "false" },
            ],
          },
        },
      ),
      n(
        "8",
        "pushNotification",
        "Push: Reminder — clothes still in washer",
        1720,
        {
          pushNotificationDetails: {
            title: "Reminder: Clothes Still in Washer",
            body: "It has been 30 minutes since your wash finished. Please move clothes to avoid musty odors.",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Sends a push notification and Slack family alert when your SmartThings-connected washer finishes its cycle. If clothes are not moved to the dryer within 30 minutes, sends a follow-up reminder to prevent musty laundry. Includes deduplication to avoid alert spam.",
  },

  // ── 320. Rain Alert Before Commute ──
  "Rain Alert Before Commute": {
    nodes: [
      trigger("Schedule: Pre-commute weather check", "schedule", {
        schedule: "Every day at commute time minus 1 hour",
        howItStarts:
          "Runs one hour before your configured commute time to check for rain and alert you to bring an umbrella or adjust plans.",
        setup:
          "Set your commute departure time and home location coordinates. Configure OpenWeatherMap API key. Set push notification device.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "OpenWeatherMap: Fetch precipitation forecast for commute window",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.openweathermap.org/data/2.5/forecast?lat={latitude}&lon={longitude}&units=imperial&cnt=4&appid={owm_api_key}",
            headers: [{ key: "Accept", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "list[0].pop",
                customName: "rain_prob_now",
                type: "number",
              },
              {
                name: "list[1].pop",
                customName: "rain_prob_commute",
                type: "number",
              },
              {
                name: "list[0].weather[0].description",
                customName: "weather_now",
                type: "string",
              },
              {
                name: "list[0].main.temp",
                customName: "temp_now",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Check precipitation probability for commute window",
        520,
        {
          runCodeDetails: {
            code: "const max_rain = Math.max(rain_prob_now, rain_prob_commute); const rain_likely = max_rain > 0.3; const rain_certain = max_rain > 0.7; const advisory = rain_certain ? 'Rain is very likely — bring an umbrella and allow extra commute time' : rain_likely ? 'Rain is possible — consider bringing an umbrella' : 'No rain expected for your commute'; return { rain_likely, rain_certain, max_rain_pct: Math.round(max_rain * 100), advisory };",
            variables: [
              {
                name: "rain_likely",
                customName: "rain_likely",
                type: "boolean",
              },
              {
                name: "rain_certain",
                customName: "rain_certain",
                type: "boolean",
              },
              {
                name: "max_rain_pct",
                customName: "max_rain_pct",
                type: "number",
              },
              { name: "advisory", customName: "advisory", type: "string" },
            ],
          },
        },
      ),
      n("4", "condition", "Rain likely during commute?", 760, {
        conditionDetails: {
          conditions: [
            { field: "rain_likely", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "pushNotification", "Push: Rain alert — bring umbrella", 1000, {
        pushNotificationDetails: {
          title: "Rain Alert: {max_rain_pct}% chance",
          body: "{advisory}. Current: {weather_now}, {temp_now}F.",
        },
      }),
      emailNode(
        "6",
        "Email: Commute weather summary",
        1240,
        "{user_email}",
        "Commute Weather: {weather_now} — {advisory}",
        "<h2>Commute Weather Check</h2><p><strong>Current Conditions:</strong> {weather_now}, {temp_now}F</p><p><strong>Rain Probability:</strong> {max_rain_pct}%</p><p><strong>Advisory:</strong> {advisory}</p>",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log commute weather data",
        1480,
        "Commute_Weather",
        '[["{timestamp}","{weather_now}","{temp_now}","{max_rain_pct}","{rain_likely}","{advisory}"]]',
      ),
      n(
        "8",
        "runCode",
        "Suggest commute adjustments based on conditions",
        1720,
        {
          runCodeDetails: {
            code: "const leave_early = rain_certain || temp_now < 25; const suggestion = leave_early ? 'Consider leaving 15 minutes early for weather delays' : rain_likely ? 'Normal commute time should be fine, but bring rain gear' : 'Clear commute expected'; return { leave_early, suggestion };",
            variables: [
              {
                name: "leave_early",
                customName: "leave_early",
                type: "boolean",
              },
              {
                name: "suggestion",
                customName: "commute_suggestion",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Checks the weather forecast one hour before your commute and sends a push notification rain alert when precipitation is likely. Includes commute timing suggestions and logs daily commute weather data for pattern analysis.",
  },

  // ── 321. Calendar Reminder by SMS ──
  "Calendar Reminder by SMS": {
    nodes: [
      trigger("Google Calendar: Event starting in 30 minutes", "webhook_app", {
        source: "google_calendar",
        event: "event_reminder",
        howItStarts:
          "Fires 30 minutes before a Google Calendar event starts, sending an SMS reminder to ensure you never miss a meeting.",
        setup:
          "Connect Google Calendar webhook for event reminders at the 30-minute mark. The payload includes event_title, event_time, event_location, and attendees.",
        inputFields: [],
      }),
      n("2", "runCode", "Format SMS-friendly reminder text", 280, {
        runCodeDetails: {
          code: "const is_important = (event_title || '').toLowerCase().includes('interview') || (event_title || '').toLowerCase().includes('board') || (event_title || '').toLowerCase().includes('client'); const location_text = event_location ? ` at ${event_location}` : ''; const sms_text = `Reminder: ${event_title}${location_text} in 30 min.`; return { sms_text, is_important, has_location: !!event_location };",
          variables: [
            { name: "sms_text", customName: "sms_text", type: "string" },
            {
              name: "is_important",
              customName: "is_important",
              type: "boolean",
            },
            {
              name: "has_location",
              customName: "has_location",
              type: "boolean",
            },
          ],
        },
      }),
      smsNode(
        "3",
        "SMS: Send calendar event reminder",
        520,
        "{user_phone}",
        "{sms_text}",
      ),
      n("4", "condition", "Important meeting requiring extra alert?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_important", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "5",
        "pushNotification",
        "Push: Extra alert for important meeting",
        1000,
        {
          pushNotificationDetails: {
            title: "IMPORTANT: {event_title} in 30 min",
            body: "Location: {event_location}. Make sure you're prepared!",
          },
        },
      ),
      sheetsNode(
        "6",
        "Google Sheets: Log reminder sent",
        1240,
        "Reminder_Log",
        '[["{timestamp}","{event_title}","{event_time}","{is_important}","sms_sent"]]',
      ),
      n("7", "runCode", "Check for scheduling conflicts", 1480, {
        runCodeDetails: {
          code: "const has_conflict = false; const conflict_note = has_conflict ? 'Conflict detected — check your calendar' : 'No conflicts'; return { has_conflict, conflict_note };",
          variables: [
            {
              name: "has_conflict",
              customName: "has_conflict",
              type: "boolean",
            },
            {
              name: "conflict_note",
              customName: "conflict_note",
              type: "string",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Meeting prep reminder if rescheduled",
        1720,
        "{user_email}",
        "Upcoming: {event_title} at {event_time}",
        "<p>Your event <strong>{event_title}</strong> starts at {event_time}.</p><p>Location: {event_location}</p><p>{conflict_note}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Sends an SMS reminder 30 minutes before every Google Calendar event. Important meetings (interviews, board meetings, client calls) get an additional push notification. Logs all reminders and checks for scheduling conflicts.",
  },

  // ── 322. Birthday Reminder to Phone or Speaker ──
  "Birthday Reminder to Phone or Speaker": {
    nodes: [
      trigger("Schedule: Daily birthday check", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs every morning to check Google Contacts for birthdays today and send reminders with gift suggestions.",
        setup:
          "Connect Google People API to access contact birthdays. Configure push notification device and optionally connect Google Home for voice announcements.",
        inputFields: [],
      }),
      n("2", "hitApi", "Google Contacts: Fetch birthdays for today", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://people.googleapis.com/v1/people/me/connections?personFields=names,birthdays&pageSize=1000",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            { name: "connections", customName: "contacts_raw", type: "string" },
            {
              name: "totalItems",
              customName: "total_contacts",
              type: "number",
            },
          ],
        },
      }),
      n("3", "condition", "Any birthdays today?", 520, {
        conditionDetails: {
          conditions: [
            {
              field: "birthday_names",
              operator: "is_not_empty",
              value: "true",
            },
          ],
        },
      }),
      n("4", "pushNotification", "Push: Birthday reminder notification", 760, {
        pushNotificationDetails: {
          title: "Birthday Today: {birthday_names}",
          body: "Don't forget to wish {birthday_names} a happy birthday! Check suggestions in your email.",
        },
      }),
      n(
        "5",
        "hitApi",
        "Google Home: Announce birthday reminder on smart speaker",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://homegraph.googleapis.com/v1/devices:reportStateAndNotification",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "announcement",
                value: "Reminder: Today is {birthday_names}'s birthday!",
              },
            ],
            variables: [
              {
                name: "status",
                customName: "announcement_status",
                type: "string",
              },
            ],
          },
        },
      ),
      emailNode(
        "6",
        "Email: Birthday details with gift suggestions",
        1240,
        "{user_email}",
        "Birthday Today: {birthday_names}",
        "<h2>Birthday Reminder</h2><p>Today is <strong>{birthday_names}</strong>'s birthday!</p><p><strong>Quick Actions:</strong></p><ul><li>Send a text or call</li><li>Post on their social media</li><li>Order a gift card</li></ul>",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log birthday reminders sent",
        1480,
        "Birthday_Log",
        '[["{timestamp}","{birthday_names}","reminded"]]',
      ),
      n("8", "runCode", "Check for upcoming birthdays this week", 1720, {
        runCodeDetails: {
          code: "const social_post_reminder = 'Consider posting a birthday message on social media'; return { social_post_reminder, week_ahead_note: 'Check Google Calendar for upcoming birthdays this week' };",
          variables: [
            {
              name: "social_post_reminder",
              customName: "social_post_reminder",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Checks Google Contacts each morning for birthdays and sends a push notification, smart speaker announcement, and email with gift suggestions. Never miss a birthday again with multi-channel reminders.",
  },

  // ── 323. Grocery List from Voice Assistant to Notes ──
  "Grocery List from Voice Assistant to Notes": {
    nodes: [
      trigger("Google Assistant: Grocery item spoken", "webhook_app", {
        source: "google_assistant",
        event: "grocery_voice_command",
        howItStarts:
          "Fires when you tell Google Assistant to add an item to your grocery list, syncing it to Todoist and Google Keep.",
        setup:
          "Configure Google Assistant webhook for grocery-related voice commands. Connect Todoist API with your grocery project ID. Connect Google Keep for backup sync.",
        inputFields: [],
      }),
      n("2", "runCode", "Parse spoken items and normalize quantities", 280, {
        runCodeDetails: {
          code: "const raw = voice_text || ''; const items = raw.split(' and ').map(i => i.trim()).filter(i => i.length > 0); const item_count = items.length; const formatted_items = items.join(', '); return { parsed_items: formatted_items, item_count, first_item: items[0] || raw };",
          variables: [
            {
              name: "parsed_items",
              customName: "parsed_items",
              type: "string",
            },
            { name: "item_count", customName: "item_count", type: "number" },
            { name: "first_item", customName: "first_item", type: "string" },
          ],
        },
      }),
      n("3", "hitApi", "Todoist: Add items to grocery list project", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.todoist.com/rest/v2/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {todoist_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "content", value: "{parsed_items}" },
            { key: "project_id", value: "{grocery_project_id}" },
            { key: "labels", value: '["grocery"]' },
          ],
          variables: [
            { name: "id", customName: "todoist_task_id", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Duplicate item already on list?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_duplicate", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "runCode", "Merge quantities if duplicate detected", 1000, {
        runCodeDetails: {
          code: "const merged = `${first_item} (updated quantity)`; return { merged_item: merged, merge_action: 'quantity_updated' };",
          variables: [
            { name: "merged_item", customName: "merged_item", type: "string" },
            {
              name: "merge_action",
              customName: "merge_action",
              type: "string",
            },
          ],
        },
      }),
      n("6", "pushNotification", "Push: Items added to grocery list", 1240, {
        pushNotificationDetails: {
          title: "Added to Grocery List",
          body: "{item_count} item(s) added: {parsed_items}",
        },
      }),
      sheetsNode(
        "7",
        "Google Sheets: Log grocery list additions",
        1480,
        "Grocery_Log",
        '[["{timestamp}","{parsed_items}","{item_count}","voice_assistant"]]',
      ),
      n("8", "hitApi", "Google Keep: Sync grocery items to shared note", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://keep.googleapis.com/v1/notes/{grocery_note_id}/items",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "text", value: "{parsed_items}" }],
          variables: [
            { name: "status", customName: "keep_sync_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures grocery items spoken to Google Assistant, parses them, and adds to your Todoist grocery project. Handles duplicates by merging quantities, sends a confirmation push notification, and syncs to Google Keep for a shared family grocery list.",
  },

  // ── 324. Location-Based Do-Not-Disturb ──
  "Location-Based Do-Not-Disturb": {
    nodes: [
      trigger("IFTTT: Entered quiet zone geofence", "webhook_app", {
        source: "ifttt",
        event: "geofence_enter_quiet_zone",
        howItStarts:
          "Fires when your phone enters a quiet zone geofence (church, theater, gym, library), automatically enabling Do Not Disturb mode.",
        setup:
          "Set up IFTTT geofences around your regular quiet zones. Include location_name and location_type (church/theater/gym/library) in the webhook payload.",
        inputFields: [],
      }),
      n("2", "runCode", "Identify location type and set DND parameters", 280, {
        runCodeDetails: {
          code: "const durations = { church: 90, theater: 150, gym: 75, library: 120, default: 60 }; const dnd_duration = durations[location_type] || durations.default; const entry_time = new Date().toISOString(); return { dnd_duration, entry_time, dnd_label: `DND: ${location_name}` };",
          variables: [
            {
              name: "dnd_duration",
              customName: "dnd_duration",
              type: "number",
            },
            { name: "entry_time", customName: "entry_time", type: "string" },
            { name: "dnd_label", customName: "dnd_label", type: "string" },
          ],
        },
      }),
      n("3", "hitApi", "Android: Enable Do Not Disturb mode", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://autoremote.googleapis.com/sendmessage",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "key", value: "{autoremote_key}" },
            { key: "message", value: "enable_dnd=:={dnd_duration}" },
          ],
          variables: [
            {
              name: "status",
              customName: "dnd_enabled_status",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "4",
        "Google Sheets: Log DND activation",
        760,
        "DND_Log",
        '[["{entry_time}","{location_name}","{location_type}","enabled","{dnd_duration}"]]',
      ),
      n("5", "delayAction", "Wait until leaving quiet zone", 1000, {
        delayDetails: { duration: 2, unit: "hours" },
      }),
      n("6", "hitApi", "Android: Disable Do Not Disturb", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://autoremote.googleapis.com/sendmessage",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "key", value: "{autoremote_key}" },
            { key: "message", value: "disable_dnd" },
          ],
          variables: [
            {
              name: "status",
              customName: "dnd_disabled_status",
              type: "string",
            },
          ],
        },
      }),
      n(
        "7",
        "pushNotification",
        "Push: DND mode disabled — back to normal",
        1480,
        {
          pushNotificationDetails: {
            title: "DND Off",
            body: "Do Not Disturb has been turned off after leaving {location_name}. You may have missed notifications.",
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log DND deactivation",
        1720,
        "DND_Log",
        '[["{timestamp}","{location_name}","{location_type}","disabled","{dnd_duration}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically activates Do Not Disturb when you enter quiet zones like churches, theaters, gyms, or libraries via IFTTT geofencing. Sets duration based on location type and restores normal mode when you leave. Logs all DND events.",
  },

  // ── 325. Geofence Open Garage or Gate ──
  "Geofence Open Garage or Gate": {
    nodes: [
      trigger(
        "IFTTT: Geofence triggered — approaching home driveway",
        "webhook_app",
        {
          source: "ifttt",
          event: "geofence_approaching_driveway",
          howItStarts:
            "Fires when your phone enters the driveway approach geofence (200m radius), automatically opening the garage door.",
          setup:
            "Set up a tight IFTTT geofence around your driveway approach. Connect MyQ garage door API. Include device_id and driver_id for authorization verification.",
          inputFields: [],
        },
      ),
      n("2", "runCode", "Verify authorized driver", 280, {
        runCodeDetails: {
          code: "const authorized_drivers = ['driver_1', 'driver_2', 'driver_3']; const is_authorized = authorized_drivers.includes(driver_id); const verify_time = new Date().toISOString(); return { is_authorized, verify_time, driver_label: is_authorized ? 'Authorized' : 'Unauthorized' };",
          variables: [
            {
              name: "is_authorized",
              customName: "is_authorized",
              type: "boolean",
            },
            { name: "verify_time", customName: "verify_time", type: "string" },
            {
              name: "driver_label",
              customName: "driver_label",
              type: "string",
            },
          ],
        },
      }),
      n("3", "condition", "Authorized driver?", 520, {
        conditionDetails: {
          conditions: [
            { field: "is_authorized", operator: "equals", value: "true" },
          ],
        },
      }),
      n("4", "hitApi", "MyQ: Open garage door", 760, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.myq.com/api/v5.1/Accounts/{myq_account}/Devices/{garage_device_id}/actions",
          headers: [
            { key: "Authorization", value: "Bearer {myq_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "action_type", value: "open" }],
          variables: [
            {
              name: "status",
              customName: "garage_open_status",
              type: "string",
            },
          ],
        },
      }),
      n("5", "delayAction", "Wait 5 minutes then auto-close", 1000, {
        delayDetails: { duration: 5, unit: "minutes" },
      }),
      n("6", "hitApi", "MyQ: Auto-close garage door", 1240, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.myq.com/api/v5.1/Accounts/{myq_account}/Devices/{garage_device_id}/actions",
          headers: [
            { key: "Authorization", value: "Bearer {myq_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "action_type", value: "close" }],
          variables: [
            {
              name: "status",
              customName: "garage_close_status",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "7",
        "Google Sheets: Log garage door events",
        1480,
        "Garage_Log",
        '[["{verify_time}","{driver_label}","{garage_open_status}","{garage_close_status}"]]',
      ),
      n("8", "pushNotification", "Push: Garage door status update", 1720, {
        pushNotificationDetails: {
          title: "Garage Door Update",
          body: "Opened for {driver_label} arrival. Auto-closing in 5 minutes.",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically opens your garage door when an authorized driver approaches the driveway via IFTTT geofence. Verifies authorization, opens the door, then auto-closes after 5 minutes for security. Logs all garage events and sends status notifications.",
  },

  // ── 326. Bedtime Smart Home Shutdown ──
  "Bedtime Smart Home Shutdown": {
    nodes: [
      trigger("Schedule: Nightly bedtime routine activation", "schedule", {
        schedule: "Every day at 10:30 PM",
        howItStarts:
          "Runs at your configured bedtime to systematically shut down smart home devices — lights off, doors locked, thermostat to night mode.",
        setup:
          "Set your bedtime schedule. Connect Philips Hue, Nest thermostat, and SmartThings hub for door locks and appliance control.",
        inputFields: [],
      }),
      n("2", "hitApi", "Hue: Turn off all lights", 280, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{hue_bridge_ip}/api/{hue_api_key}/groups/0/action",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [{ key: "on", value: "false" }],
          variables: [
            { name: "status", customName: "lights_off_status", type: "string" },
          ],
        },
      }),
      n("3", "hitApi", "Nest: Set thermostat to night/sleep mode", 520, {
        hitApiDetails: {
          method: "PUT",
          url: "https://smartdevicemanagement.googleapis.com/v1/enterprises/{nest_project}/devices/{thermostat_id}:executeCommand",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "command",
              value:
                "sdm.devices.commands.ThermostatTemperatureSetpoint.SetHeat",
            },
            { key: "params", value: '{"heatCelsius":18.5}' },
          ],
          variables: [
            { name: "status", customName: "nest_night_status", type: "string" },
          ],
        },
      }),
      n("4", "hitApi", "SmartThings: Lock all doors", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.smartthings.com/v1/devices/{lock_device_id}/commands",
          headers: [
            { key: "Authorization", value: "Bearer {smartthings_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "commands",
              value:
                '[{"component":"main","capability":"lock","command":"lock"}]',
            },
          ],
          variables: [
            {
              name: "results[0].status",
              customName: "lock_status",
              type: "string",
            },
          ],
        },
      }),
      n("5", "condition", "TV or media still on?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "tv_power_state", operator: "equals", value: "on" },
          ],
        },
      }),
      n("6", "hitApi", "SmartThings: Turn off TV and media devices", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.smartthings.com/v1/devices/{tv_device_id}/commands",
          headers: [
            { key: "Authorization", value: "Bearer {smartthings_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "commands",
              value:
                '[{"component":"main","capability":"switch","command":"off"}]',
            },
          ],
          variables: [
            {
              name: "results[0].status",
              customName: "tv_off_status",
              type: "string",
            },
          ],
        },
      }),
      n("7", "pushNotification", "Push: Goodnight — home secured", 1480, {
        pushNotificationDetails: {
          title: "Goodnight — Home Secured",
          body: "Lights: off. Doors: locked. Thermostat: night mode. Sleep well!",
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log bedtime routine execution",
        1720,
        "Bedtime_Log",
        '[["{timestamp}","{lights_off_status}","{nest_night_status}","{lock_status}","bedtime_routine"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Runs your nightly bedtime routine: turns off all Philips Hue lights, sets Nest thermostat to sleep temperature, locks all SmartThings-connected doors, and shuts off any media devices still running. Sends a goodnight push notification confirming your home is secured.",
  },

  // ── 327. Morning Routine Scene Activation ──
  "Morning Routine Scene Activation": {
    nodes: [
      trigger("Schedule: Morning alarm time routine", "schedule", {
        schedule: "Every weekday at 6:30 AM",
        howItStarts:
          "Runs at your alarm time on weekdays to gradually wake you up with lights, comfortable temperature, news, and your morning schedule.",
        setup:
          "Set your weekday alarm time. Connect Philips Hue for sunrise simulation, Nest for temperature, Sonos for audio, and Google Calendar for schedule preview.",
        inputFields: [],
      }),
      n("2", "hitApi", "Hue: Activate sunrise simulation scene", 280, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{hue_bridge_ip}/api/{hue_api_key}/groups/{bedroom_group_id}/action",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "on", value: "true" },
            { key: "bri", value: "150" },
            { key: "ct", value: "350" },
            { key: "transitiontime", value: "6000" },
          ],
          variables: [
            { name: "status", customName: "sunrise_status", type: "string" },
          ],
        },
      }),
      n(
        "3",
        "hitApi",
        "Nest: Set thermostat to comfortable morning temperature",
        520,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://smartdevicemanagement.googleapis.com/v1/enterprises/{nest_project}/devices/{thermostat_id}:executeCommand",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "command",
                value:
                  "sdm.devices.commands.ThermostatTemperatureSetpoint.SetHeat",
              },
              { key: "params", value: '{"heatCelsius":21.0}' },
            ],
            variables: [
              {
                name: "status",
                customName: "nest_morning_status",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "hitApi", "Sonos: Play morning news briefing", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.ws.sonos.com/control/api/v1/households/{household_id}/groups/{group_id}/favorites",
          headers: [
            { key: "Authorization", value: "Bearer {sonos_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "favoriteId", value: "{news_briefing_favorite_id}" },
            { key: "playOnCompletion", value: "true" },
          ],
          variables: [
            { name: "status", customName: "sonos_status", type: "string" },
          ],
        },
      }),
      n("5", "condition", "Is it a workday?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_workday", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "6",
        "hitApi",
        "Google Calendar: Fetch first meeting of the day",
        1240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin={today_start}&timeMax={today_end}&maxResults=3&orderBy=startTime&singleEvents=true",
            headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
            body: [],
            variables: [
              {
                name: "items[0].summary",
                customName: "first_meeting",
                type: "string",
              },
              {
                name: "items[0].start.dateTime",
                customName: "first_meeting_time",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "7",
        "pushNotification",
        "Push: Good morning with today's schedule preview",
        1480,
        {
          pushNotificationDetails: {
            title: "Good Morning",
            body: "First meeting: {first_meeting} at {first_meeting_time}. Sunrise lights on, news playing.",
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log morning routine execution",
        1720,
        "Morning_Log",
        '[["{timestamp}","{sunrise_status}","{nest_morning_status}","{sonos_status}","{first_meeting}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Activates your morning wake-up routine: gradual Hue sunrise simulation, comfortable Nest temperature, Sonos news briefing, and a push notification with your first meeting. Workdays include a Google Calendar schedule preview to start your day informed.",
  },

  // ── 328. Air Quality Alert ──
  "Air Quality Alert": {
    nodes: [
      trigger("Schedule: Hourly air quality monitoring", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Runs hourly to check air quality index (AQI) and alert you when conditions are unhealthy for outdoor activities.",
        setup:
          "Set your location ZIP code. Configure AirNow API key. Set AQI thresholds for moderate (100) and unhealthy (150) alerts.",
        inputFields: [],
      }),
      n("2", "hitApi", "AirNow: Fetch current AQI reading", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.airnowapi.org/aq/observation/zipCode/current/?format=application/json&zipCode={zip_code}&API_KEY={airnow_key}",
          headers: [{ key: "Accept", value: "application/json" }],
          body: [],
          variables: [
            { name: "[0].AQI", customName: "aqi_value", type: "number" },
            {
              name: "[0].Category.Name",
              customName: "aqi_category",
              type: "string",
            },
            {
              name: "[0].ParameterName",
              customName: "pollutant",
              type: "string",
            },
          ],
        },
      }),
      n(
        "3",
        "runCode",
        "Evaluate AQI thresholds and generate health guidance",
        520,
        {
          runCodeDetails: {
            code: "const moderate = aqi_value > 100; const unhealthy = aqi_value > 150; const dangerous = aqi_value > 200; const guidance = dangerous ? 'Avoid all outdoor activity. Close windows.' : unhealthy ? 'Limit prolonged outdoor exertion. Sensitive groups should stay indoors.' : moderate ? 'Unusually sensitive people should reduce outdoor exertion.' : 'Air quality is good. Enjoy outdoor activities.'; return { moderate, unhealthy, dangerous, guidance, alert_level: dangerous ? 'danger' : unhealthy ? 'unhealthy' : moderate ? 'moderate' : 'good' };",
            variables: [
              { name: "moderate", customName: "is_moderate", type: "boolean" },
              {
                name: "unhealthy",
                customName: "is_unhealthy",
                type: "boolean",
              },
              {
                name: "guidance",
                customName: "health_guidance",
                type: "string",
              },
              {
                name: "alert_level",
                customName: "alert_level",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "AQI above moderate threshold?", 760, {
        conditionDetails: {
          conditions: [
            { field: "aqi_value", operator: "greater_than", value: "100" },
          ],
        },
      }),
      n("5", "pushNotification", "Push: Air quality alert notification", 1000, {
        pushNotificationDetails: {
          title: "Air Quality: {aqi_category} (AQI {aqi_value})",
          body: "{health_guidance}. Primary pollutant: {pollutant}.",
        },
      }),
      emailNode(
        "6",
        "Email: Detailed health guidance for poor air quality",
        1240,
        "{user_email}",
        "Air Quality Alert: AQI {aqi_value} — {aqi_category}",
        "<h2>Air Quality Alert</h2><p><strong>AQI:</strong> {aqi_value} ({aqi_category})</p><p><strong>Pollutant:</strong> {pollutant}</p><p><strong>Guidance:</strong> {health_guidance}</p>",
      ),
      n("7", "condition", "AQI above unhealthy threshold?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "aqi_value", operator: "greater_than", value: "150" },
          ],
        },
      }),
      slackNode(
        "8",
        "Slack: Family urgent air quality alert",
        1720,
        "🚨 *Air Quality UNHEALTHY: AQI {aqi_value}*\n{health_guidance}\nPollutant: {pollutant}\nStay indoors and close windows.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Monitors air quality hourly via AirNow API and sends graduated alerts based on AQI levels. Moderate AQI triggers a push notification, while unhealthy levels escalate to email guidance and a family Slack alert. Includes specific health recommendations based on pollutant type.",
  },

  // ── 329. Plant Watering Reminder from Weather Conditions ──
  "Plant Watering Reminder from Weather Conditions": {
    nodes: [
      trigger("Schedule: Daily morning plant watering check", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs each morning to check the rain forecast and remind you to water your plants only if no rain is expected.",
        setup:
          "Configure OpenWeatherMap API with your location. Set the rain probability threshold (default: 30% or less means you should water).",
        inputFields: [],
      }),
      n("2", "hitApi", "OpenWeatherMap: Fetch rain forecast for today", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.openweathermap.org/data/2.5/forecast?lat={latitude}&lon={longitude}&units=imperial&cnt=8&appid={owm_api_key}",
          headers: [{ key: "Accept", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "list[0].pop",
              customName: "rain_prob_morning",
              type: "number",
            },
            {
              name: "list[4].pop",
              customName: "rain_prob_afternoon",
              type: "number",
            },
            {
              name: "list[0].main.temp",
              customName: "morning_temp",
              type: "number",
            },
          ],
        },
      }),
      n("3", "runCode", "Check if rain is expected today", 520, {
        runCodeDetails: {
          code: "const max_rain = Math.max(rain_prob_morning, rain_prob_afternoon); const no_rain_expected = max_rain < 0.3; const is_hot = morning_temp > 85; const water_urgency = is_hot && no_rain_expected ? 'high' : no_rain_expected ? 'normal' : 'skip'; return { no_rain_expected, max_rain_pct: Math.round(max_rain * 100), water_urgency, water_message: no_rain_expected ? (is_hot ? 'Hot and dry — water thoroughly today!' : 'No rain expected — time to water your plants') : 'Rain expected today — skip watering' };",
          variables: [
            {
              name: "no_rain_expected",
              customName: "no_rain_expected",
              type: "boolean",
            },
            {
              name: "max_rain_pct",
              customName: "max_rain_pct",
              type: "number",
            },
            {
              name: "water_urgency",
              customName: "water_urgency",
              type: "string",
            },
            {
              name: "water_message",
              customName: "water_message",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "No rain expected — should water plants?", 760, {
        conditionDetails: {
          conditions: [
            { field: "no_rain_expected", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "pushNotification", "Push: Water your plants today", 1000, {
        pushNotificationDetails: {
          title: "Water Plants Today",
          body: "{water_message}. Rain chance: {max_rain_pct}%. Temp: {morning_temp}F.",
        },
      }),
      n("6", "delayAction", "Wait until evening for follow-up check", 1240, {
        delayDetails: { duration: 12, unit: "hours" },
      }),
      n("7", "condition", "Still no rain after full day?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "no_rain_expected", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "8",
        "pushNotification",
        "Push: Evening reminder — plants still need water",
        1720,
        {
          pushNotificationDetails: {
            title: "Reminder: Water Plants",
            body: "No rain today. If you have not watered yet, do it now before dark.",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Checks the daily rain forecast each morning and sends a plant watering reminder only when no rain is expected. Accounts for heat conditions to indicate urgency. If plants still need water by evening, sends a follow-up reminder.",
  },

  // ── 330. Package Delivery Notification from Email ──
  "Package Delivery Notification from Email": {
    nodes: [
      trigger("Email: Delivery notification email received", "email", {
        source: "gmail",
        event: "delivery_email_received",
        howItStarts:
          "Fires when an email containing delivery or shipping keywords arrives from known carriers (UPS, FedEx, USPS, Amazon).",
        setup:
          "Connect Gmail and set up filters for carrier emails. Filter by subjects containing 'shipped', 'delivered', 'out for delivery', 'tracking'.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract carrier, tracking number, and delivery status",
        280,
        "You are a package tracking assistant. Extract delivery details from the email. Return JSON with fields: carrier, tracking_number, status (shipped/in_transit/out_for_delivery/delivered), estimated_delivery, item_description, sender_name, is_out_for_delivery (true/false).",
        "Email subject: {email_subject}, Body: {email_body}, From: {sender_email}",
        [
          { field: "carrier", as: "carrier", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "status", as: "delivery_status", type: "string" },
          {
            field: "estimated_delivery",
            as: "estimated_delivery",
            type: "string",
          },
          { field: "item_description", as: "item_description", type: "string" },
          {
            field: "is_out_for_delivery",
            as: "is_out_for_delivery",
            type: "boolean",
          },
        ],
      ),
      n("3", "runCode", "Dedupe against recently tracked packages", 520, {
        runCodeDetails: {
          code: "const is_new = true; const tracking_url = carrier === 'UPS' ? `https://www.ups.com/track?tracknum=${tracking_number}` : carrier === 'FedEx' ? `https://www.fedex.com/fedextrack/?trknbr=${tracking_number}` : `https://tools.usps.com/go/TrackConfirmAction?tLabels=${tracking_number}`; return { is_new, tracking_url };",
          variables: [
            { name: "is_new", customName: "is_new_package", type: "boolean" },
            {
              name: "tracking_url",
              customName: "tracking_url",
              type: "string",
            },
          ],
        },
      }),
      n("4", "pushNotification", "Push: Package delivery alert", 760, {
        pushNotificationDetails: {
          title: "Package: {delivery_status}",
          body: "{carrier}: {item_description}. {delivery_status}. ETA: {estimated_delivery}.",
        },
      }),
      n("5", "condition", "Out for delivery today?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_out_for_delivery", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "6",
        "pushNotification",
        "Push: Package arriving today — be ready!",
        1240,
        {
          pushNotificationDetails: {
            title: "Arriving Today: {item_description}",
            body: "{carrier} package is out for delivery. Track: {tracking_url}",
          },
        },
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log package tracking data",
        1480,
        "Package_Log",
        '[["{timestamp}","{carrier}","{tracking_number}","{delivery_status}","{item_description}","{estimated_delivery}"]]',
      ),
      n(
        "8",
        "hitApi",
        "Google Calendar: Add expected delivery to calendar",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "summary",
                value: "Package: {item_description} ({carrier})",
              },
              { key: "description", value: "Tracking: {tracking_url}" },
            ],
            variables: [
              { name: "id", customName: "cal_event_id", type: "string" },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors your email for delivery notifications from carriers, extracts tracking details using AI, and sends push alerts for every status update. When a package is out for delivery, sends an extra urgent notification. Logs all packages and adds delivery dates to Google Calendar.",
  },

  // ── 331. Bills Due Reminder from Calendar or Sheet ──
  "Bills Due Reminder from Calendar or Sheet": {
    nodes: [
      trigger("Schedule: Daily morning bill check", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs each morning to check your bills spreadsheet for upcoming due dates and send reminders for bills due within the next 3 days.",
        setup:
          "Maintain a Google Sheet with columns: bill_name, amount, due_date, status (pending/paid), autopay (yes/no). Configure the spreadsheet ID and sheet name.",
        inputFields: [],
      }),
      n("2", "hitApi", "Google Sheets: Fetch upcoming bills", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Bills!A2:E50",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            { name: "values", customName: "bills_raw", type: "string" },
            {
              name: "values.length",
              customName: "total_bills",
              type: "number",
            },
          ],
        },
      }),
      n("3", "runCode", "Filter bills due within 3 days", 520, {
        runCodeDetails: {
          code: "const today = new Date(); const threshold = new Date(today.getTime() + 3 * 86400000); const bills = typeof bills_raw === 'string' ? JSON.parse(bills_raw) : bills_raw; const due_soon = bills ? bills.filter(b => new Date(b[2]) <= threshold && b[3] === 'pending') : []; const overdue = bills ? bills.filter(b => new Date(b[2]) < today && b[3] === 'pending') : []; const due_names = due_soon.map(b => b[0]).join(', '); const due_total = due_soon.reduce((sum, b) => sum + parseFloat(b[1] || 0), 0); const has_due = due_soon.length > 0; const has_overdue = overdue.length > 0; return { has_due, has_overdue, due_count: due_soon.length, due_names, due_total: Math.round(due_total * 100) / 100 };",
          variables: [
            { name: "has_due", customName: "has_due", type: "boolean" },
            { name: "has_overdue", customName: "has_overdue", type: "boolean" },
            { name: "due_count", customName: "due_count", type: "number" },
            { name: "due_names", customName: "due_names", type: "string" },
            { name: "due_total", customName: "due_total", type: "number" },
          ],
        },
      }),
      n("4", "condition", "Any bills due within 3 days?", 760, {
        conditionDetails: {
          conditions: [{ field: "has_due", operator: "equals", value: "true" }],
        },
      }),
      n("5", "pushNotification", "Push: Bills due soon reminder", 1000, {
        pushNotificationDetails: {
          title: "Bills Due: {due_count} bill(s) — ${due_total}",
          body: "Due soon: {due_names}. Pay now to avoid late fees.",
        },
      }),
      emailNode(
        "6",
        "Email: Detailed bill summary",
        1240,
        "{user_email}",
        "Bill Reminder: {due_count} bill(s) due — ${due_total} total",
        "<h2>Upcoming Bills</h2><p><strong>Due within 3 days:</strong> {due_names}</p><p><strong>Total amount:</strong> ${due_total}</p><p>Log in to your accounts to make payments or verify autopay settings.</p>",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Mark bills as reminded",
        1480,
        "Bill_Reminders",
        '[["{timestamp}","{due_names}","{due_total}","{due_count}","reminded"]]',
      ),
      smsNode(
        "8",
        "SMS: Overdue bill urgent alert",
        1720,
        "{user_phone}",
        "OVERDUE: You have bills past due! {due_names} — ${due_total} total. Pay immediately to avoid penalties.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Scans your bills spreadsheet each morning for payments due within 3 days. Sends push and email reminders with the total amount due. Overdue bills trigger an urgent SMS alert. Never miss a payment or incur late fees.",
  },

  // ── 332. New Favorite Artist Release Alert ──
  "New Favorite Artist Release Alert": {
    nodes: [
      trigger(
        "Schedule: Weekly check for new music from followed artists",
        "schedule",
        {
          schedule: "Every Friday at 10:00 AM",
          howItStarts:
            "Runs weekly to check Spotify for new releases from your followed artists, alerting you to new albums and singles.",
          setup:
            "Connect Spotify API. The workflow checks your followed artists for any releases in the past 7 days.",
          inputFields: [],
        },
      ),
      n("2", "hitApi", "Spotify: Fetch followed artists' new releases", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.spotify.com/v1/browse/new-releases?limit=20&country=US",
          headers: [{ key: "Authorization", value: "Bearer {spotify_token}" }],
          body: [],
          variables: [
            {
              name: "albums.items",
              customName: "releases_raw",
              type: "string",
            },
            {
              name: "albums.total",
              customName: "total_releases",
              type: "number",
            },
          ],
        },
      }),
      n(
        "3",
        "runCode",
        "Filter for followed artists and detect new releases",
        520,
        {
          runCodeDetails: {
            code: "const releases = typeof releases_raw === 'string' ? JSON.parse(releases_raw) : (releases_raw || []); const new_releases = releases.slice(0, 5); const has_new = new_releases.length > 0; const release_names = new_releases.map(r => `${r.artists?.[0]?.name || 'Unknown'} - ${r.name}`).join(', '); const first_release = new_releases[0]; return { has_new, release_count: new_releases.length, release_names, first_artist: first_release?.artists?.[0]?.name || '', first_album: first_release?.name || '', first_url: first_release?.external_urls?.spotify || '' };",
            variables: [
              {
                name: "has_new",
                customName: "has_new_releases",
                type: "boolean",
              },
              {
                name: "release_count",
                customName: "release_count",
                type: "number",
              },
              {
                name: "release_names",
                customName: "release_names",
                type: "string",
              },
              {
                name: "first_artist",
                customName: "first_artist",
                type: "string",
              },
              {
                name: "first_album",
                customName: "first_album",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "New releases from followed artists?", 760, {
        conditionDetails: {
          conditions: [
            { field: "has_new_releases", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "pushNotification", "Push: New music from your artists", 1000, {
        pushNotificationDetails: {
          title: "New Music: {first_artist}",
          body: "{release_count} new release(s) this week: {release_names}",
        },
      }),
      n("6", "hitApi", "Spotify: Add top release to your queue", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.spotify.com/v1/me/player/queue?uri={first_url}",
          headers: [{ key: "Authorization", value: "Bearer {spotify_token}" }],
          body: [],
          variables: [
            { name: "status", customName: "queue_status", type: "string" },
          ],
        },
      }),
      emailNode(
        "7",
        "Email: Weekly new release digest",
        1480,
        "{user_email}",
        "New Music Friday: {release_count} new releases from your artists",
        "<h2>New Music This Week</h2><p>{release_names}</p><p>Top pick <strong>{first_artist} — {first_album}</strong> has been added to your queue.</p>",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log weekly music releases",
        1720,
        "Music_Releases",
        '[["{timestamp}","{release_count}","{release_names}","{first_artist}","{first_album}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Checks Spotify weekly for new releases from your followed artists. Sends a push notification and email digest with new albums and singles, and automatically adds the top pick to your playback queue. Never miss a release from your favorite artists.",
  },

  // ── 333. Game Event Alert ──
  "Game Event Alert": {
    nodes: [
      trigger("Schedule: Daily sports game check", "schedule", {
        schedule: "Every day at 9:00 AM",
        howItStarts:
          "Runs daily to check if your favorite team has a game today and sends pre-game alerts with details.",
        setup:
          "Configure your favorite team ID for the ESPN or Sports API. Set push notification preferences and family notification channel.",
        inputFields: [],
      }),
      n("2", "hitApi", "Sports API: Check team schedule for today", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://site.api.espn.com/apis/site/v2/sports/{sport}/leagues/{league}/teams/{team_id}/schedule",
          headers: [{ key: "Accept", value: "application/json" }],
          body: [],
          variables: [
            { name: "events[0].name", customName: "game_name", type: "string" },
            { name: "events[0].date", customName: "game_time", type: "string" },
            {
              name: "events[0].competitions[0].venue.fullName",
              customName: "venue",
              type: "string",
            },
            {
              name: "events[0].competitions[0].broadcasts[0].names[0]",
              customName: "broadcast_channel",
              type: "string",
            },
          ],
        },
      }),
      n("3", "runCode", "Check if game is today and format details", 520, {
        runCodeDetails: {
          code: "const today = new Date().toISOString().split('T')[0]; const game_date = game_time ? game_time.split('T')[0] : ''; const game_today = today === game_date; const game_hour = game_time ? new Date(game_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''; return { game_today, game_hour, game_summary: game_today ? `${game_name} at ${game_hour} on ${broadcast_channel}` : 'No game today' };",
          variables: [
            { name: "game_today", customName: "game_today", type: "boolean" },
            { name: "game_hour", customName: "game_hour", type: "string" },
            {
              name: "game_summary",
              customName: "game_summary",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Game scheduled for today?", 760, {
        conditionDetails: {
          conditions: [
            { field: "game_today", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "pushNotification", "Push: Game day alert", 1000, {
        pushNotificationDetails: {
          title: "Game Day: {game_name}",
          body: "Kickoff at {game_hour}. Watch on {broadcast_channel}. Venue: {venue}.",
        },
      }),
      n("6", "hitApi", "Google Calendar: Add game to calendar", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "{game_name}" },
            {
              key: "description",
              value: "Watch on {broadcast_channel}. Venue: {venue}",
            },
            { key: "start", value: '{"dateTime":"{game_time}"}' },
          ],
          variables: [
            { name: "id", customName: "cal_event_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Notify family about today's game",
        1480,
        "🏈 *Game Day!* {game_name}\nTime: {game_hour} | Channel: {broadcast_channel} | Venue: {venue}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log game schedule tracking",
        1720,
        "Game_Tracker",
        '[["{timestamp}","{game_name}","{game_hour}","{venue}","{broadcast_channel}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Checks daily for your favorite team's game schedule. On game days, sends a push alert with kickoff time and broadcast channel, adds the event to Google Calendar, and notifies your family Slack channel. Tracks the entire season in Google Sheets.",
  },

  // ── 334. Security Camera Motion to Phone Alert ──
  "Security Camera Motion to Phone Alert": {
    nodes: [
      trigger("Ring: Motion detected by security camera", "webhook_app", {
        source: "ring",
        event: "motion_detected",
        howItStarts:
          "Fires when your Ring security camera detects motion, alerting you immediately when nobody should be home.",
        setup:
          "Connect Ring API webhook for motion events. Include camera_id, motion_zone, and snapshot_url in the payload. Configure occupancy detection.",
        inputFields: [],
      }),
      n("2", "runCode", "Check if anyone is home", 280, {
        runCodeDetails: {
          code: "const nobody_home = occupancy_status === 'empty' || occupancy_status === 'away'; const time_of_day = new Date().getHours(); const is_night = time_of_day >= 22 || time_of_day <= 5; const alert_priority = nobody_home && is_night ? 'critical' : nobody_home ? 'high' : 'low'; return { nobody_home, is_night, alert_priority };",
          variables: [
            { name: "nobody_home", customName: "nobody_home", type: "boolean" },
            { name: "is_night", customName: "is_night", type: "boolean" },
            {
              name: "alert_priority",
              customName: "alert_priority",
              type: "string",
            },
          ],
        },
      }),
      n("3", "condition", "Nobody home — suspicious activity?", 520, {
        conditionDetails: {
          conditions: [
            { field: "nobody_home", operator: "equals", value: "true" },
          ],
        },
      }),
      n("4", "pushNotification", "Push: Motion detected — nobody home", 760, {
        pushNotificationDetails: {
          title: "Motion Alert: {motion_zone}",
          body: "Motion detected by {camera_id} while nobody is home. Priority: {alert_priority}. Check camera feed.",
        },
      }),
      emailNode(
        "5",
        "Email: Motion snapshot and details",
        1000,
        "{user_email}",
        "Security Alert: Motion at {motion_zone} — {alert_priority} priority",
        "<h2>Security Camera Motion Alert</h2><p><strong>Camera:</strong> {camera_id}</p><p><strong>Zone:</strong> {motion_zone}</p><p><strong>Priority:</strong> {alert_priority}</p><p><strong>Time:</strong> {timestamp}</p><p>Check your Ring app for the live feed and recorded snapshot.</p>",
      ),
      slackNode(
        "6",
        "Slack: Alert family about motion detection",
        1240,
        "🚨 *Motion Detected: {motion_zone}*\nCamera: {camera_id} | Priority: {alert_priority}\nNobody home — check Ring app for live feed.",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log motion events",
        1480,
        "Security_Log",
        '[["{timestamp}","{camera_id}","{motion_zone}","{alert_priority}","{nobody_home}"]]',
      ),
      n(
        "8",
        "delayAction",
        "Cooldown before next alert from same camera",
        1720,
        {
          delayDetails: { duration: 5, unit: "minutes" },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Sends immediate multi-channel alerts when your Ring security camera detects motion while nobody is home. Priority escalates at night. Alerts go to push, email, and family Slack channel. Includes a cooldown to prevent repeated alerts from the same zone.",
  },

  // ── 335. Doorbell Ring to Lights Flash ──
  "Doorbell Ring to Lights Flash": {
    nodes: [
      trigger("Ring: Doorbell pressed", "webhook_app", {
        source: "ring",
        event: "doorbell_ring",
        howItStarts:
          "Fires when someone presses your Ring doorbell, flashing porch lights after dark for visibility and as a visual indicator.",
        setup:
          "Connect Ring doorbell webhook. Connect Philips Hue Bridge API for porch light control. Include ring_time and camera snapshot.",
        inputFields: [],
      }),
      n("2", "runCode", "Check if it's after dark", 280, {
        runCodeDetails: {
          code: "const hour = new Date().getHours(); const after_dark = hour >= 18 || hour <= 6; return { after_dark, current_hour: hour, time_of_day: after_dark ? 'night' : 'day' };",
          variables: [
            { name: "after_dark", customName: "after_dark", type: "boolean" },
            {
              name: "current_hour",
              customName: "current_hour",
              type: "number",
            },
            { name: "time_of_day", customName: "time_of_day", type: "string" },
          ],
        },
      }),
      n("3", "condition", "After dark?", 520, {
        conditionDetails: {
          conditions: [
            { field: "after_dark", operator: "equals", value: "true" },
          ],
        },
      }),
      n("4", "hitApi", "Hue: Flash porch lights bright white", 760, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{hue_bridge_ip}/api/{hue_api_key}/lights/{porch_light_id}/state",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "on", value: "true" },
            { key: "bri", value: "254" },
            { key: "alert", value: "lselect" },
          ],
          variables: [
            { name: "status", customName: "flash_status", type: "string" },
          ],
        },
      }),
      n("5", "pushNotification", "Push: Someone at the door", 1000, {
        pushNotificationDetails: {
          title: "Doorbell: Visitor at Front Door",
          body: "Someone rang your doorbell. Porch lights flashing. Check Ring app for video.",
        },
      }),
      sheetsNode(
        "6",
        "Google Sheets: Log doorbell events",
        1240,
        "Doorbell_Log",
        '[["{timestamp}","{time_of_day}","{after_dark}","{flash_status}","doorbell_ring"]]',
      ),
      n("7", "delayAction", "Wait 5 minutes then restore lights", 1480, {
        delayDetails: { duration: 5, unit: "minutes" },
      }),
      n("8", "hitApi", "Hue: Restore porch light to previous state", 1720, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{hue_bridge_ip}/api/{hue_api_key}/lights/{porch_light_id}/state",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "alert", value: "none" },
            { key: "bri", value: "{previous_brightness}" },
          ],
          variables: [
            { name: "status", customName: "restore_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When someone rings your Ring doorbell after dark, flashes the porch Philips Hue lights to full brightness as a visual indicator. Sends a push notification to check the Ring video feed. Restores lights to their previous state after 5 minutes.",
  },

  // ── 336. Leak Detector to Emergency SMS ──
  "Leak Detector to Emergency SMS": {
    nodes: [
      trigger("SmartThings: Water leak sensor triggered", "webhook_app", {
        source: "smartthings",
        event: "water_leak_detected",
        howItStarts:
          "Fires immediately when a SmartThings water leak sensor detects moisture, triggering emergency alerts and optional water shutoff.",
        setup:
          "Connect SmartThings hub with water leak sensors. Configure Twilio SMS for emergency contacts. Optionally connect a smart water shutoff valve.",
        inputFields: [],
      }),
      n("2", "runCode", "Verify sensor and determine severity", 280, {
        runCodeDetails: {
          code: "const sensor_location = sensor_name || 'Unknown location'; const is_critical = ['basement', 'water_heater', 'kitchen'].some(loc => sensor_location.toLowerCase().includes(loc)); const severity = is_critical ? 'CRITICAL' : 'WARNING'; return { sensor_location, is_critical, severity, alert_time: new Date().toISOString() };",
          variables: [
            {
              name: "sensor_location",
              customName: "sensor_location",
              type: "string",
            },
            { name: "is_critical", customName: "is_critical", type: "boolean" },
            { name: "severity", customName: "severity", type: "string" },
            { name: "alert_time", customName: "alert_time", type: "string" },
          ],
        },
      }),
      smsNode(
        "3",
        "SMS: Emergency water leak alert to all contacts",
        520,
        "{emergency_phone}",
        "WATER LEAK {severity}: Detected at {sensor_location} at {alert_time}. Check immediately! If away, call plumber: {plumber_phone}",
      ),
      n("4", "pushNotification", "Push: Urgent water leak detected", 760, {
        pushNotificationDetails: {
          title: "WATER LEAK: {sensor_location}",
          body: "{severity} — Leak detected. Check immediately. SMS sent to emergency contacts.",
        },
      }),
      emailNode(
        "5",
        "Email: Leak details with plumber contact info",
        1000,
        "{user_email}",
        "URGENT: Water Leak at {sensor_location}",
        "<h2 style='color:red'>Water Leak Detected</h2><p><strong>Location:</strong> {sensor_location}</p><p><strong>Severity:</strong> {severity}</p><p><strong>Time:</strong> {alert_time}</p><p><strong>Emergency Plumber:</strong> {plumber_phone}</p><p>If you are not home, call your plumber immediately to prevent water damage.</p>",
      ),
      slackNode(
        "6",
        "Slack: Family urgent water leak alert",
        1240,
        "🚨🚨 *WATER LEAK DETECTED: {sensor_location}*\nSeverity: {severity}\nTime: {alert_time}\n\nCheck immediately! Plumber: {plumber_phone}",
      ),
      n("7", "condition", "Smart water shutoff valve available?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "is_critical", operator: "equals", value: "true" },
          ],
        },
      }),
      n("8", "hitApi", "SmartThings: Emergency water shutoff", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.smartthings.com/v1/devices/{water_valve_id}/commands",
          headers: [
            { key: "Authorization", value: "Bearer {smartthings_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "commands",
              value:
                '[{"component":"main","capability":"valve","command":"close"}]',
            },
          ],
          variables: [
            {
              name: "results[0].status",
              customName: "shutoff_status",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers emergency multi-channel alerts when a SmartThings water leak sensor detects moisture. Sends SMS to emergency contacts, push notification, email with plumber info, and family Slack alert. Critical locations (basement, water heater) trigger automatic smart water valve shutoff to minimize damage.",
  },

  // ── 337. Fridge or Temperature Sensor Alert ──
  "Fridge or Temperature Sensor Alert": {
    nodes: [
      trigger("SmartThings: Temperature threshold exceeded", "webhook_app", {
        source: "smartthings",
        event: "temperature_threshold",
        howItStarts:
          "Fires when a SmartThings temperature sensor detects readings above your set threshold (e.g., fridge above 45F or freezer above 10F).",
        setup:
          "Connect SmartThings temperature sensors on fridge/freezer. Set threshold values. Include sensor_name, current_temp, and threshold_temp in the payload.",
        inputFields: [],
      }),
      n("2", "runCode", "Check if threshold breach is sustained", 280, {
        runCodeDetails: {
          code: "const temp_diff = current_temp - threshold_temp; const is_freezer = (sensor_name || '').toLowerCase().includes('freezer'); const is_sustained = duration_minutes > 15; const urgency = is_freezer ? 'critical' : is_sustained ? 'high' : 'moderate'; return { temp_diff, is_freezer, is_sustained, urgency, alert_message: `${sensor_name}: ${current_temp}F (threshold: ${threshold_temp}F, ${temp_diff}F over)` };",
          variables: [
            { name: "temp_diff", customName: "temp_diff", type: "number" },
            { name: "is_freezer", customName: "is_freezer", type: "boolean" },
            {
              name: "is_sustained",
              customName: "is_sustained",
              type: "boolean",
            },
            { name: "urgency", customName: "urgency", type: "string" },
            {
              name: "alert_message",
              customName: "alert_message",
              type: "string",
            },
          ],
        },
      }),
      n(
        "3",
        "condition",
        "Temperature sustained above threshold for 15+ minutes?",
        520,
        {
          conditionDetails: {
            conditions: [
              { field: "is_sustained", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      n("4", "pushNotification", "Push: Temperature alert for appliance", 760, {
        pushNotificationDetails: {
          title: "Temp Alert: {sensor_name}",
          body: "{alert_message}. Urgency: {urgency}. Check appliance immediately.",
        },
      }),
      smsNode(
        "5",
        "SMS: Urgent freezer temperature alert",
        1000,
        "{user_phone}",
        "URGENT: {sensor_name} at {current_temp}F (limit {threshold_temp}F). Food may be at risk. Check immediately!",
      ),
      emailNode(
        "6",
        "Email: Temperature monitoring maintenance reminder",
        1240,
        "{user_email}",
        "Temperature Alert: {sensor_name} — {current_temp}F",
        "<h2>Appliance Temperature Alert</h2><p><strong>Sensor:</strong> {sensor_name}</p><p><strong>Current:</strong> {current_temp}F (Threshold: {threshold_temp}F)</p><p><strong>Duration:</strong> Sustained for 15+ minutes</p><p><strong>Action:</strong> Check if door is ajar, coils need cleaning, or appliance needs service.</p>",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log temperature anomaly",
        1480,
        "Temp_Alerts",
        '[["{timestamp}","{sensor_name}","{current_temp}","{threshold_temp}","{temp_diff}","{urgency}","{is_sustained}"]]',
      ),
      n("8", "runCode", "Suppress repeat alerts for same sensor", 1720, {
        runCodeDetails: {
          code: "const suppress_until = new Date(Date.now() + 30 * 60000).toISOString(); return { suppress_until, suppression_active: true, note: 'Next alert suppressed for 30 minutes to avoid spam' };",
          variables: [
            {
              name: "suppress_until",
              customName: "suppress_until",
              type: "string",
            },
            {
              name: "suppression_active",
              customName: "suppression_active",
              type: "boolean",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors SmartThings temperature sensors on fridges and freezers. When temperature exceeds the threshold for 15+ minutes, sends graduated alerts: push notification for all, urgent SMS for freezers. Includes alert suppression to prevent notification spam and logs all anomalies.",
  },

  // ── 338. Power Outage or UPS Alert ──
  "Power Outage or UPS Alert": {
    nodes: [
      trigger("UPS: Switched to battery power mode", "webhook_app", {
        source: "ups_monitor",
        event: "battery_mode_activated",
        howItStarts:
          "Fires when your UPS (uninterruptible power supply) switches to battery mode, indicating a power outage at your location.",
        setup:
          "Connect UPS network monitoring (NUT, APC, CyberPower) webhook. The payload includes ups_name, battery_level, estimated_runtime_min, and event_type.",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Classify outage severity based on battery level",
        280,
        {
          runCodeDetails: {
            code: "const severity = battery_level < 20 ? 'critical' : battery_level < 50 ? 'warning' : 'monitoring'; const safe_shutdown_needed = estimated_runtime_min < 10; const outage_time = new Date().toISOString(); return { severity, safe_shutdown_needed, outage_time, status_message: `Power outage detected. UPS at ${battery_level}%, ~${estimated_runtime_min}min runtime` };",
            variables: [
              {
                name: "severity",
                customName: "outage_severity",
                type: "string",
              },
              {
                name: "safe_shutdown_needed",
                customName: "safe_shutdown_needed",
                type: "boolean",
              },
              {
                name: "outage_time",
                customName: "outage_time",
                type: "string",
              },
              {
                name: "status_message",
                customName: "status_message",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "pushNotification", "Push: Power outage alert", 520, {
        pushNotificationDetails: {
          title: "Power Outage: {outage_severity}",
          body: "{status_message}",
        },
      }),
      smsNode(
        "4",
        "SMS: Alert emergency contact about outage",
        760,
        "{emergency_phone}",
        "Power outage at home. UPS: {battery_level}%, {estimated_runtime_min}min remaining. Severity: {outage_severity}.",
      ),
      emailNode(
        "5",
        "Email: Power outage details and utility report link",
        1000,
        "{user_email}",
        "Power Outage Alert — UPS at {battery_level}%",
        "<h2>Power Outage Detected</h2><p>{status_message}</p><p><strong>Severity:</strong> {outage_severity}</p><p><strong>Battery:</strong> {battery_level}%</p><p><strong>Runtime:</strong> ~{estimated_runtime_min} minutes</p><p>Check your utility provider for outage updates.</p>",
      ),
      n("6", "condition", "Extended outage — battery critically low?", 1240, {
        conditionDetails: {
          conditions: [
            {
              field: "safe_shutdown_needed",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      sheetsNode(
        "7",
        "Google Sheets: Log power outage event",
        1480,
        "Power_Log",
        '[["{outage_time}","{outage_severity}","{battery_level}","{estimated_runtime_min}","{safe_shutdown_needed}"]]',
      ),
      n("8", "runCode", "Trigger safe device shutdown if critical", 1720, {
        runCodeDetails: {
          code: "const devices_to_save = safe_shutdown_needed ? ['NAS', 'desktop', 'media_server'] : []; return { devices_to_save: devices_to_save.join(', '), shutdown_triggered: safe_shutdown_needed, note: safe_shutdown_needed ? 'Emergency shutdown initiated for connected devices' : 'Monitoring — no shutdown needed yet' };",
          variables: [
            {
              name: "devices_to_save",
              customName: "devices_to_save",
              type: "string",
            },
            {
              name: "shutdown_triggered",
              customName: "shutdown_triggered",
              type: "boolean",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Monitors your UPS for power outages and sends immediate alerts via push, SMS, and email with battery status and estimated runtime. When battery drops critically low, triggers safe shutdown of connected devices to prevent data loss. Logs all outage events.",
  },

  // ── 339. Commute Time Traffic Alert ──
  "Commute Time Traffic Alert": {
    nodes: [
      trigger("Schedule: Pre-commute traffic check", "schedule", {
        schedule: "Weekdays at commute time minus 45 minutes",
        howItStarts:
          "Runs 45 minutes before your commute to check Google Maps traffic and alert you if you need to leave early.",
        setup:
          "Configure your home and work addresses. Set your normal departure time. Connect Google Maps Directions API for real-time traffic.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Google Maps: Fetch current commute time with traffic",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://maps.googleapis.com/maps/api/directions/json?origin={home_address}&destination={work_address}&departure_time=now&traffic_model=best_guess&key={google_maps_key}",
            headers: [{ key: "Accept", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "routes[0].legs[0].duration_in_traffic.value",
                customName: "traffic_duration_sec",
                type: "number",
              },
              {
                name: "routes[0].legs[0].duration.value",
                customName: "normal_duration_sec",
                type: "number",
              },
              {
                name: "routes[0].summary",
                customName: "route_name",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compare current vs normal commute and detect delays",
        520,
        {
          runCodeDetails: {
            code: "const traffic_min = Math.round(traffic_duration_sec / 60); const normal_min = Math.round(normal_duration_sec / 60); const delay_min = traffic_min - normal_min; const delay_pct = Math.round((delay_min / normal_min) * 100); const significant_delay = delay_pct > 20; const severe_delay = delay_pct > 50; const leave_early_min = significant_delay ? delay_min + 5 : 0; return { traffic_min, normal_min, delay_min, delay_pct, significant_delay, severe_delay, leave_early_min, commute_summary: `${traffic_min}min (normally ${normal_min}min, +${delay_min}min)` };",
            variables: [
              {
                name: "traffic_min",
                customName: "traffic_min",
                type: "number",
              },
              { name: "normal_min", customName: "normal_min", type: "number" },
              { name: "delay_min", customName: "delay_min", type: "number" },
              { name: "delay_pct", customName: "delay_pct", type: "number" },
              {
                name: "significant_delay",
                customName: "significant_delay",
                type: "boolean",
              },
              {
                name: "commute_summary",
                customName: "commute_summary",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Commute more than 20% longer than normal?", 760, {
        conditionDetails: {
          conditions: [
            { field: "significant_delay", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "5",
        "pushNotification",
        "Push: Leave early — traffic delay detected",
        1000,
        {
          pushNotificationDetails: {
            title: "Traffic Alert: +{delay_min}min",
            body: "Commute: {commute_summary}. Leave {leave_early_min}min early via {route_name}.",
          },
        },
      ),
      emailNode(
        "6",
        "Email: Route alternatives for heavy traffic",
        1240,
        "{user_email}",
        "Commute Traffic: {commute_summary}",
        "<h2>Commute Traffic Alert</h2><p><strong>Current estimate:</strong> {traffic_min} minutes</p><p><strong>Normal time:</strong> {normal_min} minutes</p><p><strong>Delay:</strong> +{delay_min} minutes ({delay_pct}% longer)</p><p><strong>Route:</strong> {route_name}</p><p><strong>Recommendation:</strong> Leave {leave_early_min} minutes earlier than usual.</p>",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log commute traffic data",
        1480,
        "Commute_Traffic",
        '[["{timestamp}","{traffic_min}","{normal_min}","{delay_min}","{delay_pct}","{route_name}"]]',
      ),
      smsNode(
        "8",
        "SMS: Severe delay alert if 50%+ longer",
        1720,
        "{user_phone}",
        "SEVERE TRAFFIC: Commute {traffic_min}min (normally {normal_min}). Delay: +{delay_min}min. Leave NOW or consider alternatives.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Checks Google Maps traffic 45 minutes before your commute and alerts you when delays exceed 20% of normal travel time. Push notification with leave-early recommendation, email with route details, and SMS for severe delays over 50%. Tracks commute patterns in Google Sheets.",
  },

  // ── 340. Fitness Activity to Spreadsheet or Journal ──
  "Fitness Activity to Spreadsheet or Journal": {
    nodes: [
      trigger("Strava: New activity completed", "webhook_app", {
        source: "strava",
        event: "activity_created",
        howItStarts:
          "Fires when you complete and upload a new fitness activity to Strava (run, ride, swim, etc.), logging it to your personal fitness tracker.",
        setup:
          "Connect Strava webhook API for activity.create events. The payload includes activity_id, type, distance, moving_time, and elapsed_time.",
        inputFields: [],
      }),
      n("2", "runCode", "Extract and format activity metrics", 280, {
        runCodeDetails: {
          code: "const distance_mi = Math.round(distance_meters / 1609.34 * 100) / 100; const duration_min = Math.round(moving_time_sec / 60); const pace_min_per_mi = duration_min / distance_mi; const pace_formatted = `${Math.floor(pace_min_per_mi)}:${String(Math.round((pace_min_per_mi % 1) * 60)).padStart(2, '0')}`; const calories_est = Math.round(duration_min * 10); const is_pb = false; return { distance_mi, duration_min, pace_formatted, calories_est, is_pb, activity_summary: `${activity_type}: ${distance_mi}mi in ${duration_min}min (${pace_formatted}/mi)` };",
          variables: [
            { name: "distance_mi", customName: "distance_mi", type: "number" },
            {
              name: "duration_min",
              customName: "duration_min",
              type: "number",
            },
            {
              name: "pace_formatted",
              customName: "pace_formatted",
              type: "string",
            },
            {
              name: "calories_est",
              customName: "calories_est",
              type: "number",
            },
            { name: "is_pb", customName: "is_pb", type: "boolean" },
            {
              name: "activity_summary",
              customName: "activity_summary",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "3",
        "Google Sheets: Append activity to fitness log",
        520,
        "Fitness_Log",
        '[["{timestamp}","{activity_type}","{distance_mi}","{duration_min}","{pace_formatted}","{calories_est}","{activity_name}"]]',
      ),
      n("4", "condition", "Personal best achieved?", 760, {
        conditionDetails: {
          conditions: [{ field: "is_pb", operator: "equals", value: "true" }],
        },
      }),
      n(
        "5",
        "pushNotification",
        "Push: Congratulations on personal best!",
        1000,
        {
          pushNotificationDetails: {
            title: "Personal Best! {activity_type}",
            body: "{activity_summary}. New PR — keep it up!",
          },
        },
      ),
      n("6", "hitApi", "Day One: Create journal entry for activity", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://dayone.me/api/v1/entries",
          headers: [
            { key: "Authorization", value: "Bearer {dayone_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "text",
              value:
                "## {activity_type} — {timestamp}\n\n{activity_summary}\n\nCalories: ~{calories_est}\nHow I felt: [add notes]",
            },
            { key: "tags", value: '["fitness","{activity_type}"]' },
          ],
          variables: [
            { name: "id", customName: "journal_entry_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Share workout achievement",
        1480,
        "🏃 *{activity_type} Complete*\n{activity_summary}\nCalories: ~{calories_est} | Logged to fitness tracker.",
      ),
      n("8", "runCode", "Compute monthly fitness statistics", 1720, {
        runCodeDetails: {
          code: "const month_total_est = distance_mi * 4; return { month_distance_est: month_total_est, weekly_target: 15, on_target: distance_mi >= 3, stat_note: `Monthly pace: ~${month_total_est}mi if sustained` };",
          variables: [
            {
              name: "month_distance_est",
              customName: "month_distance_est",
              type: "number",
            },
            { name: "on_target", customName: "on_target", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Logs every Strava activity to Google Sheets with formatted metrics (distance, pace, calories). Creates a Day One journal entry for personal reflection, celebrates personal bests with push notifications, and shares achievements on Slack. Tracks monthly fitness trends.",
  },

  // ── 341. Sleep Tracker to Log ──
  "Sleep Tracker to Log": {
    nodes: [
      trigger("Fitbit: Sleep data synced", "webhook_app", {
        source: "fitbit",
        event: "sleep_logged",
        howItStarts:
          "Fires when your Fitbit syncs sleep data for the previous night, logging sleep metrics and providing insights.",
        setup:
          "Connect Fitbit API webhook for sleep events. The payload includes sleep_duration, deep_sleep, rem_sleep, light_sleep, awake_time, and sleep_score.",
        inputFields: [],
      }),
      n("2", "runCode", "Extract and evaluate sleep metrics", 280, {
        runCodeDetails: {
          code: "const total_hours = Math.round(sleep_duration_min / 60 * 10) / 10; const deep_pct = Math.round((deep_sleep_min / sleep_duration_min) * 100); const rem_pct = Math.round((rem_sleep_min / sleep_duration_min) * 100); const poor_sleep = total_hours < 6 || sleep_score < 70; const sleep_quality = sleep_score >= 85 ? 'Excellent' : sleep_score >= 70 ? 'Good' : sleep_score >= 55 ? 'Fair' : 'Poor'; const tip = poor_sleep ? 'Try earlier bedtime, reduce screens 1h before bed, keep room cool' : 'Great sleep! Maintain your routine.'; return { total_hours, deep_pct, rem_pct, poor_sleep, sleep_quality, tip };",
          variables: [
            { name: "total_hours", customName: "total_hours", type: "number" },
            { name: "deep_pct", customName: "deep_pct", type: "number" },
            { name: "rem_pct", customName: "rem_pct", type: "number" },
            { name: "poor_sleep", customName: "poor_sleep", type: "boolean" },
            {
              name: "sleep_quality",
              customName: "sleep_quality",
              type: "string",
            },
            { name: "tip", customName: "sleep_tip", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "3",
        "Google Sheets: Append sleep data to tracker",
        520,
        "Sleep_Log",
        '[["{timestamp}","{total_hours}","{sleep_score}","{deep_pct}","{rem_pct}","{sleep_quality}"]]',
      ),
      n("4", "condition", "Poor sleep detected?", 760, {
        conditionDetails: {
          conditions: [
            { field: "poor_sleep", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "pushNotification", "Push: Sleep quality insight with tip", 1000, {
        pushNotificationDetails: {
          title: "Sleep: {sleep_quality} ({total_hours}h)",
          body: "Score: {sleep_score}. Deep: {deep_pct}%, REM: {rem_pct}%. Tip: {sleep_tip}",
        },
      }),
      n("6", "runCode", "Compute weekly sleep trend analysis", 1240, {
        runCodeDetails: {
          code: "const weekly_target = 7 * 7; const on_track = total_hours >= 7; const trend_note = on_track ? 'Meeting sleep goals' : 'Below recommended 7+ hours'; return { on_track, trend_note, weekly_avg_est: total_hours };",
          variables: [
            { name: "on_track", customName: "on_track", type: "boolean" },
            { name: "trend_note", customName: "trend_note", type: "string" },
          ],
        },
      }),
      emailNode(
        "7",
        "Email: Weekly sleep report summary",
        1480,
        "{user_email}",
        "Sleep Report: {sleep_quality} — {total_hours} hours",
        "<h2>Sleep Report</h2><p><strong>Duration:</strong> {total_hours} hours</p><p><strong>Quality:</strong> {sleep_quality} (Score: {sleep_score})</p><p><strong>Deep Sleep:</strong> {deep_pct}%</p><p><strong>REM Sleep:</strong> {rem_pct}%</p><p><strong>Tip:</strong> {sleep_tip}</p><p><strong>Trend:</strong> {trend_note}</p>",
      ),
      n("8", "hitApi", "Day One: Journal sleep entry", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://dayone.me/api/v1/entries",
          headers: [
            { key: "Authorization", value: "Bearer {dayone_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "text",
              value:
                "## Sleep Log — {timestamp}\n\nDuration: {total_hours}h | Quality: {sleep_quality} | Score: {sleep_score}\nDeep: {deep_pct}% | REM: {rem_pct}%\n\n{sleep_tip}",
            },
            { key: "tags", value: '["sleep","health"]' },
          ],
          variables: [{ name: "id", customName: "journal_id", type: "string" }],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Logs Fitbit sleep data each morning to Google Sheets with quality scores, deep and REM sleep percentages. Sends a push notification with sleep tips when quality is poor, emails a weekly summary, and creates a Day One journal entry for long-term health tracking.",
  },

  // ── 342. Smart Plug Energy Threshold Alert ──
  "Smart Plug Energy Threshold Alert": {
    nodes: [
      trigger(
        "SmartThings: Smart plug power reading exceeds threshold",
        "webhook_app",
        {
          source: "smartthings",
          event: "power_threshold_exceeded",
          howItStarts:
            "Fires when a SmartThings smart plug reports power consumption above your configured threshold, indicating potential waste or anomaly.",
          setup:
            "Connect SmartThings energy monitoring smart plugs. Set power thresholds per device. Payload includes device_name, power_watts, threshold_watts.",
          inputFields: [],
        },
      ),
      n("2", "runCode", "Evaluate power usage against threshold", 280, {
        runCodeDetails: {
          code: "const over_pct = Math.round(((power_watts - threshold_watts) / threshold_watts) * 100); const is_anomaly = over_pct > 50; const monthly_cost_est = Math.round(power_watts * 24 * 30 / 1000 * 0.12 * 100) / 100; return { over_pct, is_anomaly, monthly_cost_est, usage_note: `${device_name}: ${power_watts}W (${over_pct}% over ${threshold_watts}W threshold)` };",
          variables: [
            { name: "over_pct", customName: "over_pct", type: "number" },
            { name: "is_anomaly", customName: "is_anomaly", type: "boolean" },
            {
              name: "monthly_cost_est",
              customName: "monthly_cost_est",
              type: "number",
            },
            { name: "usage_note", customName: "usage_note", type: "string" },
          ],
        },
      }),
      n("3", "condition", "Power significantly over threshold?", 520, {
        conditionDetails: {
          conditions: [
            {
              field: "power_watts",
              operator: "greater_than",
              value: "{threshold_watts}",
            },
          ],
        },
      }),
      n("4", "pushNotification", "Push: High energy usage alert", 760, {
        pushNotificationDetails: {
          title: "High Power: {device_name}",
          body: "{usage_note}. Est. monthly cost: ${monthly_cost_est}.",
        },
      }),
      emailNode(
        "5",
        "Email: Monthly energy comparison for device",
        1000,
        "{user_email}",
        "Energy Alert: {device_name} — {power_watts}W ({over_pct}% over threshold)",
        "<h2>Energy Usage Alert</h2><p>{usage_note}</p><p><strong>Monthly Cost Estimate:</strong> ${monthly_cost_est}</p><p>Consider turning off the device or checking for malfunction if usage seems abnormal.</p>",
      ),
      sheetsNode(
        "6",
        "Google Sheets: Log power reading",
        1240,
        "Energy_Log",
        '[["{timestamp}","{device_name}","{power_watts}","{threshold_watts}","{over_pct}","{monthly_cost_est}","{is_anomaly}"]]',
      ),
      n("7", "condition", "Anomalous usage pattern?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "is_anomaly", operator: "equals", value: "true" },
          ],
        },
      }),
      smsNode(
        "8",
        "SMS: Anomaly alert — possible malfunction",
        1720,
        "{user_phone}",
        "ENERGY ANOMALY: {device_name} drawing {power_watts}W ({over_pct}% over normal). Possible malfunction — check device.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "6", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Monitors SmartThings smart plug energy readings and alerts you when consumption exceeds your threshold. Calculates monthly cost impact, logs data for trend analysis, and escalates to SMS when anomalous patterns suggest a device malfunction.",
  },

  // ── 343. Voice Note to Task List ──
  "Voice Note to Task List": {
    nodes: [
      trigger("Google Assistant: Voice note captured", "webhook_app", {
        source: "google_assistant",
        event: "voice_note",
        howItStarts:
          "Fires when you dictate a voice note to Google Assistant, automatically parsing it into actionable tasks in Todoist.",
        setup:
          "Configure Google Assistant to forward voice notes via webhook. Connect Todoist API with your default project ID. Connect OpenAI for task extraction.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Transcribe and parse tasks from voice note",
        280,
        "You are a personal productivity assistant. Parse the voice note and extract distinct actionable tasks. Identify urgency, due dates, and categories. Return JSON with fields: tasks (comma-separated), task_count, has_urgent (true/false), categories (comma-separated), first_task, first_due_date (if mentioned, otherwise 'none').",
        "Voice note: {voice_text}",
        [
          { field: "tasks", as: "extracted_tasks", type: "string" },
          { field: "task_count", as: "task_count", type: "number" },
          { field: "has_urgent", as: "has_urgent", type: "boolean" },
          { field: "categories", as: "categories", type: "string" },
          { field: "first_task", as: "first_task", type: "string" },
        ],
      ),
      n("3", "runCode", "Format tasks for Todoist creation", 520, {
        runCodeDetails: {
          code: "const taskList = extracted_tasks.split(',').map(t => t.trim()); const priority = has_urgent ? 4 : 3; return { formatted_tasks: taskList.join('\\n'), priority, task_list: taskList, batch_label: `Voice note: ${task_count} tasks` };",
          variables: [
            {
              name: "formatted_tasks",
              customName: "formatted_tasks",
              type: "string",
            },
            { name: "priority", customName: "priority", type: "number" },
            { name: "batch_label", customName: "batch_label", type: "string" },
          ],
        },
      }),
      n("4", "hitApi", "Todoist: Create tasks from voice note", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.todoist.com/rest/v2/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {todoist_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "content", value: "{first_task}" },
            { key: "description", value: "From voice note: {formatted_tasks}" },
            { key: "priority", value: "{priority}" },
            { key: "project_id", value: "{default_project_id}" },
          ],
          variables: [
            { name: "id", customName: "todoist_task_id", type: "string" },
          ],
        },
      }),
      n("5", "condition", "Urgent tasks detected?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_urgent", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "6",
        "pushNotification",
        "Push: Urgent task created from voice note",
        1240,
        {
          pushNotificationDetails: {
            title: "Urgent Task Added",
            body: "{first_task}. {task_count} total task(s) from voice note.",
          },
        },
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log voice note task extraction",
        1480,
        "Voice_Tasks",
        '[["{timestamp}","{task_count}","{categories}","{has_urgent}","{first_task}"]]',
      ),
      emailNode(
        "8",
        "Email: Daily task summary with voice note additions",
        1720,
        "{user_email}",
        "Voice Note: {task_count} task(s) added to Todoist",
        "<h2>Tasks from Voice Note</h2><p><strong>Tasks Created:</strong> {task_count}</p><pre>{formatted_tasks}</pre><p><strong>Categories:</strong> {categories}</p><p>All tasks added to your Todoist inbox.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures voice notes from Google Assistant, uses AI to parse them into distinct actionable tasks, and creates them in Todoist with appropriate priority. Urgent tasks trigger an immediate push notification. Logs all voice-to-task conversions and emails a daily summary.",
  },

  // ── 344. Recipe Bookmark to Grocery List ──
  "Recipe Bookmark to Grocery List": {
    nodes: [
      trigger("Instapaper/Pocket: Recipe saved or bookmarked", "webhook_app", {
        source: "pocket",
        event: "recipe_saved",
        howItStarts:
          "Fires when you save a recipe to Instapaper or Pocket, automatically extracting ingredients and adding them to your grocery list.",
        setup:
          "Connect Pocket/Instapaper webhook for saved items. Use tag 'recipe' to filter. Connect OpenAI for ingredient extraction and Todoist for grocery list.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract ingredients from saved recipe",
        280,
        "You are a culinary assistant. Extract all ingredients from the recipe page. Normalize quantities and combine duplicates. Return JSON with fields: recipe_name, servings, ingredients (comma-separated with quantities), special_ingredients (uncommon items comma-separated), ingredient_count, cuisine_type, prep_time_min.",
        "Recipe URL: {saved_url}, Title: {saved_title}",
        [
          { field: "recipe_name", as: "recipe_name", type: "string" },
          { field: "ingredients", as: "ingredients", type: "string" },
          {
            field: "special_ingredients",
            as: "special_ingredients",
            type: "string",
          },
          { field: "ingredient_count", as: "ingredient_count", type: "number" },
          { field: "cuisine_type", as: "cuisine_type", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Normalize quantities and format for grocery list",
        520,
        {
          runCodeDetails: {
            code: "const items = ingredients.split(',').map(i => i.trim()); const has_special = special_ingredients && special_ingredients.length > 0; return { formatted_items: items.join('\\n'), item_count: items.length, has_special, grocery_note: `For: ${recipe_name} (${cuisine_type})` };",
            variables: [
              {
                name: "formatted_items",
                customName: "formatted_items",
                type: "string",
              },
              { name: "item_count", customName: "item_count", type: "number" },
              {
                name: "has_special",
                customName: "has_special",
                type: "boolean",
              },
              {
                name: "grocery_note",
                customName: "grocery_note",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "hitApi", "Todoist: Add ingredients to grocery list", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.todoist.com/rest/v2/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {todoist_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "content", value: "Groceries for {recipe_name}" },
            { key: "description", value: "{formatted_items}" },
            { key: "project_id", value: "{grocery_project_id}" },
            { key: "labels", value: '["grocery","recipe"]' },
          ],
          variables: [{ name: "id", customName: "todoist_id", type: "string" }],
        },
      }),
      n("5", "condition", "Special or hard-to-find ingredients?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_special", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "6",
        "pushNotification",
        "Push: Note about specialty ingredients",
        1240,
        {
          pushNotificationDetails: {
            title: "Specialty Ingredients Needed",
            body: "For {recipe_name}: {special_ingredients}. You may need to visit a specialty store.",
          },
        },
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log recipe to meal plan",
        1480,
        "Meal_Plan",
        '[["{timestamp}","{recipe_name}","{cuisine_type}","{ingredient_count}","{saved_url}"]]',
      ),
      n("8", "hitApi", "Google Keep: Sync grocery list update", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://keep.googleapis.com/v1/notes/{grocery_note_id}/items",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "text", value: "--- {recipe_name} ---\n{formatted_items}" },
          ],
          variables: [
            { name: "status", customName: "keep_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When you save a recipe to Pocket or Instapaper, AI extracts all ingredients and adds them to your Todoist grocery list and Google Keep. Flags specialty ingredients needing a special trip, and logs the recipe to your meal planning sheet.",
  },

  // ── 345. Vacation Mode Home Security Workflow ──
  "Vacation Mode Home Security Workflow": {
    nodes: [
      trigger("Google Calendar: Vacation event detected", "webhook_app", {
        source: "google_calendar",
        event: "vacation_event_created",
        howItStarts:
          "Fires when a vacation or travel event is created in Google Calendar, automatically arming home security and simulating occupancy.",
        setup:
          "Tag vacation events in Google Calendar with 'vacation' or 'travel'. Connect Hue, Nest, and Ring for home automation. Configure neighbor notification email.",
        inputFields: [],
      }),
      n("2", "runCode", "Activate vacation mode settings", 280, {
        runCodeDetails: {
          code: "const vacation_start = event_start; const vacation_end = event_end; const duration_days = Math.ceil((new Date(vacation_end) - new Date(vacation_start)) / 86400000); return { duration_days, vacation_start, vacation_end, mode: 'vacation_armed', random_lights: true };",
          variables: [
            {
              name: "duration_days",
              customName: "duration_days",
              type: "number",
            },
            { name: "mode", customName: "security_mode", type: "string" },
            {
              name: "random_lights",
              customName: "random_lights",
              type: "boolean",
            },
          ],
        },
      }),
      n(
        "3",
        "hitApi",
        "Hue: Schedule random light patterns to simulate occupancy",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{hue_bridge_ip}/api/{hue_api_key}/schedules",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "name", value: "Vacation Lights" },
              {
                key: "description",
                value: "Random lighting for {duration_days} days",
              },
              {
                key: "command",
                value:
                  '{"address":"/api/{hue_api_key}/groups/0/action","method":"PUT","body":{"on":true,"bri":180}}',
              },
              { key: "localtime", value: "W127/T18:30:00" },
            ],
            variables: [
              { name: "id", customName: "hue_schedule_id", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Nest: Set thermostat to away mode with monitoring",
        760,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://smartdevicemanagement.googleapis.com/v1/enterprises/{nest_project}/devices/{thermostat_id}:executeCommand",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "command",
                value: "sdm.devices.commands.ThermostatEco.SetMode",
              },
              { key: "params", value: '{"mode":"MANUAL_ECO"}' },
            ],
            variables: [
              {
                name: "status",
                customName: "nest_away_status",
                type: "string",
              },
            ],
          },
        },
      ),
      n("5", "hitApi", "Ring: Arm security system in away mode", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.ring.com/clients_api/ring_devices/{location_id}/mode",
          headers: [
            { key: "Authorization", value: "Bearer {ring_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "mode", value: "away" }],
          variables: [
            { name: "status", customName: "ring_armed_status", type: "string" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Notify neighbor about vacation with instructions",
        1240,
        "{neighbor_email}",
        "Vacation Notice: Away for {duration_days} days",
        "<p>Hi, I'll be away from {vacation_start} to {vacation_end} ({duration_days} days).</p><p>Security system is armed with random light schedules. Please let me know if you notice anything unusual. Thank you for keeping an eye out!</p>",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log vacation security activation",
        1480,
        "Security_Log",
        '[["{timestamp}","vacation_mode_on","{duration_days}","{ring_armed_status}","{nest_away_status}","{hue_schedule_id}"]]',
      ),
      n("8", "pushNotification", "Push: Home vacation mode armed", 1720, {
        pushNotificationDetails: {
          title: "Vacation Mode Armed",
          body: "Security: armed. Thermostat: eco. Lights: random schedule. Duration: {duration_days} days. Neighbor notified.",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically activates home vacation mode when a travel event appears in Google Calendar. Schedules random Hue light patterns to simulate occupancy, sets Nest to eco mode, arms Ring security, notifies your neighbor, and confirms everything via push notification.",
  },

  // ── 346. Smart Diffuser or Plug Schedule ──
  "Smart Diffuser or Plug Schedule": {
    nodes: [
      trigger("Schedule: Diffuser/plug activation times", "schedule", {
        schedule: "Every day at 7:00 AM, 12:00 PM, and 6:00 PM",
        howItStarts:
          "Runs at scheduled times to activate your smart plug (diffuser, humidifier, or other appliance) based on occupancy and time of day.",
        setup:
          "Connect SmartThings smart plug. Configure activation schedule and duration for each time slot. Connect occupancy detection.",
        inputFields: [],
      }),
      n("2", "runCode", "Check home occupancy mode", 280, {
        runCodeDetails: {
          code: "const home_mode = current_home_mode || 'home'; const is_occupied = home_mode === 'home'; const hour = new Date().getHours(); const session = hour < 10 ? 'morning' : hour < 15 ? 'midday' : 'evening'; const duration_min = session === 'morning' ? 30 : session === 'midday' ? 20 : 45; return { is_occupied, session, duration_min };",
          variables: [
            { name: "is_occupied", customName: "is_occupied", type: "boolean" },
            { name: "session", customName: "session", type: "string" },
            {
              name: "duration_min",
              customName: "duration_min",
              type: "number",
            },
          ],
        },
      }),
      n("3", "condition", "Home currently occupied?", 520, {
        conditionDetails: {
          conditions: [
            { field: "is_occupied", operator: "equals", value: "true" },
          ],
        },
      }),
      n("4", "hitApi", "SmartThings: Activate smart plug", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.smartthings.com/v1/devices/{plug_device_id}/commands",
          headers: [
            { key: "Authorization", value: "Bearer {smartthings_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "commands",
              value:
                '[{"component":"main","capability":"switch","command":"on"}]',
            },
          ],
          variables: [
            {
              name: "results[0].status",
              customName: "plug_on_status",
              type: "string",
            },
          ],
        },
      }),
      n("5", "delayAction", "Wait for session duration", 1000, {
        delayDetails: { duration: 30, unit: "minutes" },
      }),
      n("6", "hitApi", "SmartThings: Deactivate smart plug", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.smartthings.com/v1/devices/{plug_device_id}/commands",
          headers: [
            { key: "Authorization", value: "Bearer {smartthings_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "commands",
              value:
                '[{"component":"main","capability":"switch","command":"off"}]',
            },
          ],
          variables: [
            {
              name: "results[0].status",
              customName: "plug_off_status",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "7",
        "Google Sheets: Log diffuser session",
        1480,
        "Diffuser_Log",
        '[["{timestamp}","{session}","{duration_min}","{plug_on_status}","{plug_off_status}"]]',
      ),
      n("8", "runCode", "Adjust next session based on usage patterns", 1720, {
        runCodeDetails: {
          code: "const next_session = session === 'morning' ? 'midday' : session === 'midday' ? 'evening' : 'morning (tomorrow)'; return { next_session, schedule_note: `Session complete. Next: ${next_session}` };",
          variables: [
            {
              name: "next_session",
              customName: "next_session",
              type: "string",
            },
            {
              name: "schedule_note",
              customName: "schedule_note",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Schedules your smart plug (diffuser, humidifier, etc.) at multiple daily times, activating only when the home is occupied. Runs for the configured duration per time slot and auto-deactivates. Logs all sessions for usage tracking.",
  },

  // ── 347. Window or Door Open Climate Alert ──
  "Window or Door Open Climate Alert": {
    nodes: [
      trigger(
        "SmartThings: Contact sensor opened (window/door)",
        "webhook_app",
        {
          source: "smartthings",
          event: "contact_sensor_open",
          howItStarts:
            "Fires when a SmartThings contact sensor detects a window or door has been opened while the HVAC system is running.",
          setup:
            "Connect SmartThings contact sensors on key windows and doors. Connect Nest thermostat API for HVAC status. Include sensor_name and location in the payload.",
          inputFields: [],
        },
      ),
      n("2", "runCode", "Check if HVAC is currently running", 280, {
        runCodeDetails: {
          code: "const hvac_running = hvac_status === 'heating' || hvac_status === 'cooling'; const open_time = new Date().toISOString(); const energy_waste = hvac_running; return { hvac_running, open_time, energy_waste, alert_message: hvac_running ? `${sensor_name} opened while HVAC is ${hvac_status}. Close to save energy.` : `${sensor_name} opened. HVAC not running.` };",
          variables: [
            {
              name: "hvac_running",
              customName: "hvac_running",
              type: "boolean",
            },
            { name: "open_time", customName: "open_time", type: "string" },
            {
              name: "energy_waste",
              customName: "energy_waste",
              type: "boolean",
            },
            {
              name: "alert_message",
              customName: "alert_message",
              type: "string",
            },
          ],
        },
      }),
      n("3", "condition", "HVAC running while window/door open?", 520, {
        conditionDetails: {
          conditions: [
            { field: "hvac_running", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "4",
        "pushNotification",
        "Push: Close window/door — HVAC wasting energy",
        760,
        {
          pushNotificationDetails: {
            title: "Close {sensor_name}!",
            body: "{alert_message}",
          },
        },
      ),
      n("5", "delayAction", "Wait 10 minutes to check if still open", 1000, {
        delayDetails: { duration: 10, unit: "minutes" },
      }),
      n("6", "condition", "Window/door still open after 10 minutes?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "still_open", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "7",
        "pushNotification",
        "Push: Reminder — still open, pausing HVAC",
        1480,
        {
          pushNotificationDetails: {
            title: "Still Open: {sensor_name}",
            body: "{sensor_name} has been open for 10+ minutes. Pausing HVAC to save energy. Close to resume.",
          },
        },
      ),
      n("8", "hitApi", "Nest: Pause HVAC to prevent energy waste", 1720, {
        hitApiDetails: {
          method: "PUT",
          url: "https://smartdevicemanagement.googleapis.com/v1/enterprises/{nest_project}/devices/{thermostat_id}:executeCommand",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "command",
              value: "sdm.devices.commands.ThermostatMode.SetMode",
            },
            { key: "params", value: '{"mode":"OFF"}' },
          ],
          variables: [
            {
              name: "status",
              customName: "hvac_paused_status",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
    summary:
      "Detects when a window or door opens while your HVAC is running and sends an immediate push notification to close it. If still open after 10 minutes, sends a follow-up reminder and automatically pauses the HVAC to prevent energy waste.",
  },

  // ── 348. Pet Camera Motion Alert ──
  "Pet Camera Motion Alert": {
    nodes: [
      trigger("Webhook: Pet camera detects motion", "webhook_app", {
        source: "pet_camera",
        event: "motion_detected",
        howItStarts:
          "Fires when your pet camera detects motion, checking if the activity is unexpected based on your pet's schedule.",
        setup:
          "Connect your pet camera webhook (Furbo, Petcube, etc.). Include camera_name, motion_zone, and snapshot_url in the payload. Set your pet's typical active hours.",
        inputFields: [],
      }),
      n("2", "runCode", "Check motion against pet's expected schedule", 280, {
        runCodeDetails: {
          code: "const hour = new Date().getHours(); const expected_sleep = hour >= 22 || hour <= 5; const unexpected = expected_sleep; const alert_level = unexpected ? 'unexpected' : 'normal'; return { unexpected, alert_level, time_context: expected_sleep ? 'During expected sleep time' : 'During active hours', motion_time: new Date().toISOString() };",
          variables: [
            {
              name: "unexpected",
              customName: "unexpected_activity",
              type: "boolean",
            },
            { name: "alert_level", customName: "alert_level", type: "string" },
            {
              name: "time_context",
              customName: "time_context",
              type: "string",
            },
            { name: "motion_time", customName: "motion_time", type: "string" },
          ],
        },
      }),
      n("3", "condition", "Unexpected activity outside normal hours?", 520, {
        conditionDetails: {
          conditions: [
            { field: "unexpected_activity", operator: "equals", value: "true" },
          ],
        },
      }),
      n("4", "pushNotification", "Push: Pet activity alert", 760, {
        pushNotificationDetails: {
          title: "Pet Alert: {camera_name}",
          body: "Motion detected ({alert_level}). {time_context}. Check camera feed.",
        },
      }),
      emailNode(
        "5",
        "Email: Pet camera snapshot and details",
        1000,
        "{user_email}",
        "Pet Camera: Motion at {camera_name} — {time_context}",
        "<h2>Pet Camera Motion</h2><p><strong>Camera:</strong> {camera_name}</p><p><strong>Time:</strong> {motion_time}</p><p><strong>Context:</strong> {time_context}</p><p>Check your pet camera app for the live feed.</p>",
      ),
      sheetsNode(
        "6",
        "Google Sheets: Log pet activity",
        1240,
        "Pet_Activity",
        '[["{motion_time}","{camera_name}","{alert_level}","{time_context}"]]',
      ),
      n("7", "delayAction", "Cooldown before next alert", 1480, {
        delayDetails: { duration: 15, unit: "minutes" },
      }),
      n("8", "runCode", "Suppress repeated alerts from same camera", 1720, {
        runCodeDetails: {
          code: "return { cooldown_active: true, next_alert_available: new Date(Date.now() + 15 * 60000).toISOString(), note: 'Alert suppressed for 15 minutes' };",
          variables: [
            {
              name: "cooldown_active",
              customName: "cooldown_active",
              type: "boolean",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "6", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors pet camera motion and compares activity to your pet's expected schedule. Unexpected motion during sleep hours triggers push and email alerts. Normal activity is logged silently. Includes a 15-minute cooldown to prevent alert fatigue.",
  },

  // ── 349. Weather-Based Irrigation Skip ──
  "Weather-Based Irrigation Skip": {
    nodes: [
      trigger("Schedule: Pre-irrigation weather check", "schedule", {
        schedule: "Every day before scheduled irrigation time",
        howItStarts:
          "Runs before your irrigation system's scheduled start to check the rain forecast and skip watering when rain is expected.",
        setup:
          "Configure OpenWeatherMap with your location. Connect Rachio smart sprinkler API. Set rain probability threshold for skipping (default: 40%).",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "OpenWeatherMap: Fetch rain forecast for next 24 hours",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.openweathermap.org/data/2.5/forecast?lat={latitude}&lon={longitude}&units=imperial&cnt=8&appid={owm_api_key}",
            headers: [{ key: "Accept", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "list[0].pop",
                customName: "rain_prob_now",
                type: "number",
              },
              {
                name: "list[2].pop",
                customName: "rain_prob_later",
                type: "number",
              },
              {
                name: "list[0].rain.3h",
                customName: "rain_amount_mm",
                type: "number",
              },
              {
                name: "list[0].weather[0].description",
                customName: "weather_desc",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "runCode", "Determine if irrigation should be skipped", 520, {
        runCodeDetails: {
          code: "const max_rain_prob = Math.max(rain_prob_now || 0, rain_prob_later || 0); const rain_expected = max_rain_prob > 0.4; const significant_rain = (rain_amount_mm || 0) > 2; const should_skip = rain_expected || significant_rain; const water_savings = should_skip ? 15 : 0; return { should_skip, max_rain_prob_pct: Math.round(max_rain_prob * 100), water_savings, skip_reason: rain_expected ? `Rain probability ${Math.round(max_rain_prob * 100)}%` : 'No rain expected — irrigating normally' };",
          variables: [
            { name: "should_skip", customName: "should_skip", type: "boolean" },
            {
              name: "max_rain_prob_pct",
              customName: "max_rain_prob_pct",
              type: "number",
            },
            {
              name: "water_savings",
              customName: "water_savings",
              type: "number",
            },
            { name: "skip_reason", customName: "skip_reason", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Rain expected — skip irrigation?", 760, {
        conditionDetails: {
          conditions: [
            { field: "should_skip", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "hitApi", "Rachio: Skip today's irrigation zone", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.rach.io/1/public/device/{rachio_device_id}/rain_delay",
          headers: [
            { key: "Authorization", value: "Bearer {rachio_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "duration", value: "86400" }],
          variables: [
            { name: "status", customName: "skip_status", type: "string" },
          ],
        },
      }),
      n(
        "6",
        "pushNotification",
        "Push: Irrigation skipped — rain expected",
        1240,
        {
          pushNotificationDetails: {
            title: "Irrigation Skipped",
            body: "{skip_reason}. Saving ~{water_savings} gallons today. Weather: {weather_desc}.",
          },
        },
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log water savings from rain skip",
        1480,
        "Irrigation_Log",
        '[["{timestamp}","{should_skip}","{max_rain_prob_pct}","{water_savings}","{weather_desc}","{skip_reason}"]]',
      ),
      emailNode(
        "8",
        "Email: Weekly irrigation and water savings report",
        1720,
        "{user_email}",
        "Irrigation Update: {skip_reason}",
        "<h2>Irrigation Status</h2><p><strong>Decision:</strong> {skip_reason}</p><p><strong>Rain Probability:</strong> {max_rain_prob_pct}%</p><p><strong>Water Saved:</strong> ~{water_savings} gallons</p><p><strong>Weather:</strong> {weather_desc}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Checks the weather before your Rachio irrigation schedule and automatically skips watering when rain is expected. Sends a push notification confirming the skip and tracks cumulative water savings in Google Sheets. Helps conserve water and reduce your utility bill.",
  },

  // ── 350. Photo Backup from Phone Event ──
  "Photo Backup from Phone Event": {
    nodes: [
      trigger("IFTTT: New photo taken on phone", "webhook_app", {
        source: "ifttt",
        event: "new_photo_taken",
        howItStarts:
          "Fires when a new photo is taken on your phone, automatically backing it up to Google Drive and logging the event.",
        setup:
          "Set up IFTTT Android/iOS photo trigger. Connect Google Drive API for backup uploads. The payload includes photo_url, filename, and file_size_bytes.",
        inputFields: [],
      }),
      n("2", "runCode", "Check file size and validate for backup", 280, {
        runCodeDetails: {
          code: "const size_mb = Math.round(file_size_bytes / 1048576 * 100) / 100; const too_large = size_mb > 25; const is_valid = !too_large && file_size_bytes > 0; const backup_folder = `Photos_${new Date().getFullYear()}_${String(new Date().getMonth() + 1).padStart(2, '0')}`; return { size_mb, too_large, is_valid, backup_folder };",
          variables: [
            { name: "size_mb", customName: "size_mb", type: "number" },
            { name: "too_large", customName: "too_large", type: "boolean" },
            { name: "is_valid", customName: "is_valid", type: "boolean" },
            {
              name: "backup_folder",
              customName: "backup_folder",
              type: "string",
            },
          ],
        },
      }),
      n(
        "3",
        "hitApi",
        "Google Drive: Upload photo to monthly backup folder",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "name", value: "{filename}" },
              { key: "parents", value: '["{backup_folder_id}"]' },
              { key: "mimeType", value: "image/jpeg" },
            ],
            variables: [
              { name: "id", customName: "drive_file_id", type: "string" },
              { name: "webViewLink", customName: "drive_link", type: "string" },
            ],
          },
        },
      ),
      n("4", "condition", "Duplicate file detected?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_duplicate", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "runCode", "Skip duplicate upload", 1000, {
        runCodeDetails: {
          code: "return { skipped: true, skip_reason: 'Duplicate file already in backup', action: 'none' };",
          variables: [
            { name: "skipped", customName: "was_skipped", type: "boolean" },
          ],
        },
      }),
      sheetsNode(
        "6",
        "Google Sheets: Log photo backup event",
        1240,
        "Photo_Backup",
        '[["{timestamp}","{filename}","{size_mb}","{backup_folder}","{drive_file_id}"]]',
      ),
      n("7", "pushNotification", "Push: Photo backed up successfully", 1480, {
        pushNotificationDetails: {
          title: "Photo Backed Up",
          body: "{filename} ({size_mb}MB) saved to {backup_folder} on Google Drive.",
        },
      }),
      emailNode(
        "8",
        "Email: Weekly photo backup summary",
        1720,
        "{user_email}",
        "Photo Backup: {filename} uploaded to Google Drive",
        "<p>Your photo <strong>{filename}</strong> ({size_mb}MB) has been backed up to Google Drive folder <strong>{backup_folder}</strong>.</p><p><a href='{drive_link}'>View in Google Drive</a></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automatically backs up every new phone photo to a monthly folder on Google Drive via IFTTT. Validates file size, checks for duplicates, and logs every backup. Sends a push confirmation and weekly email summary of backed-up photos.",
  },
};
